import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDashboardSection } from '../hooks/useDashboardSection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar, faShoppingCart, faCog, faBars, faMoon, faSun, faTruck,
  faMobileAlt, faCheckCircle, faNairaSign, faEye, faFilter, faSearch,
  faUser, faTimes, faHeart, faBell, faHistory, faMapMarked, faStar,
  faExchangeAlt, faShoppingBag, faBox, faChartLine, faCrown, faGift,
  faTag, faBolt, faPhone, faHeadset, faAward, faTrophy,
  faMedal, faChartPie, faPercentage, faClock, faFire, faUsers, faComment,
  faSignOutAlt, faUserCircle, faQuestionCircle, faHome, faWallet, faPlus,
  faSpinner, faShieldAlt, faLock, faUniversity, faStore,
  faUpload, faSave, faExclamationTriangle, faTachometerAlt, faMapMarkerAlt,
  faMoneyBillWave
} from '@fortawesome/free-solid-svg-icons';
import { format, subDays, subHours } from 'date-fns';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, ArcElement, BarElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import Loader from './Loader';
import Toast from './Toast';
import { TableCard, DataTable, THead, Th, Tr, Td, Badge, TableEmpty, statusTone } from './ui/DataTable';
import lightLogo from '../assets/autoboy_logo2.png';
import darkLogo from '../assets/autoboy_logo3.png';
import NotificationsDropdown from './NotificationsDropdown';
import { buyerAPI, ordersAPI, userAPI, notificationsAPI, productsAPI, walletAPI, wishlistAPI, badgesAPI, reportsAPI, priceAlertsAPI, dataManagementAPI, paymentAPI, dealsAPI } from '../services/api';
import { getStoredDarkMode, setStoredDarkMode } from '../utils/theme';
import { uploadImage, validateImageFile } from '../services/imageUploadService';
import KYCVerification from './KYCVerification';
import KYCStatusBanner from './KYCStatusBanner';
import TwoFactorAuth from './TwoFactorAuth';
import Disputes from './Disputes';
import SwapDeals from './SwapDeals';
import SwapOfferForm from './SwapOfferForm';
import { useSwap } from '../context/SwapContext';
import EscrowAnalytics from './EscrowAnalytics';
import EscrowSettingsPanel from './EscrowSettingsPanel';
import BankAccountsManager from './BankAccountsManager';
import WalletWithdrawModal from './WalletWithdrawModal';
import FlashDealsStrip from './FlashDealsStrip';
import AddressManagement from './AddressManagement';
import './PremiumBuyerDashboard.css';
import '../assets/css/settings-modern.css';
import '../assets/css/wallet-modals.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, BarElement, Title, Tooltip, Legend, Filler);

// Helper functions for badge and reward system (matching backend: handlers/badges.go)
const calculateUserLevel = (points) => {
  if (points >= 10000) return 'Diamond';
  if (points >= 5000) return 'Gold';
  if (points >= 2000) return 'Silver';
  if (points >= 500) return 'Bronze';
  return 'Beginner';
};

const getNextLevelInfo = (currentPoints) => {
  if (currentPoints >= 10000) {
    return { nextLevel: 'Diamond Max', nextLevelPoints: 10000 };
  }
  if (currentPoints >= 5000) {
    return { nextLevel: 'Diamond', nextLevelPoints: 10000 };
  }
  if (currentPoints >= 2000) {
    return { nextLevel: 'Gold', nextLevelPoints: 5000 };
  }
  if (currentPoints >= 500) {
    return { nextLevel: 'Silver', nextLevelPoints: 2000 };
  }
  return { nextLevel: 'Bronze', nextLevelPoints: 500 };
};

// Premium Packaging is a flat benefit of being a premium subscriber at all
// (backend: Order.SpecialPackaging = buyer.IsPremium, set at checkout
// regardless of in-dashboard loyalty level) — so it's added to every tier
// below, not gated behind reaching a higher loyalty level.
const PREMIUM_PACKAGING_PERK = { icon: '🎁', title: 'Premium Packaging', description: 'Every order arrives in premium packaging' };

const getLevelPerks = (level) => {
  const perksByLevel = {
    'Diamond': [
      { icon: '💎', title: 'Diamond Badge', description: 'Exclusive diamond member badge' },
      PREMIUM_PACKAGING_PERK,
      { icon: '⚡', title: 'Priority Support', description: '24/7 VIP customer support' },
      { icon: '🎯', title: 'Early Access', description: 'First access to new products' }
    ],
    'Gold': [
      { icon: '🥇', title: 'Gold Badge', description: 'Premium gold member badge' },
      PREMIUM_PACKAGING_PERK,
      { icon: '💬', title: 'Priority Support', description: 'Priority customer support' },
      { icon: '🎯', title: 'Early Access', description: 'Early access to deals' }
    ],
    'Silver': [
      { icon: '🥈', title: 'Silver Badge', description: 'Silver member badge' },
      PREMIUM_PACKAGING_PERK,
      { icon: '💬', title: 'Support Access', description: 'Enhanced customer support' },
      { icon: '📦', title: 'Discounts', description: '5% discount on select items' }
    ],
    'Bronze': [
      { icon: '🥉', title: 'Bronze Badge', description: 'Bronze member badge' },
      PREMIUM_PACKAGING_PERK,
      { icon: '📦', title: 'Member Discounts', description: '2% discount on select items' }
    ],
    'Beginner': [
      { icon: '🌟', title: 'Welcome Badge', description: 'New member badge' },
      PREMIUM_PACKAGING_PERK
    ]
  };

  return perksByLevel[level] || perksByLevel['Beginner'];
};

const PremiumBuyerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    swaps, loading: swapLoading, swapMetadata,
    getInitiatedSwaps, acceptPrice, declinePrice, processSwapPayment, cancelSwap,
    confirmShipment, confirmDelivery, completeSwap,
  } = useSwap();
  const [swapOffersTab, setSwapOffersTab] = useState('p2p');
  const [showSwapOfferForm, setShowSwapOfferForm] = useState(false);
  const [swapOfferProductId, setSwapOfferProductId] = useState('');
  const [swapActionLoading, setSwapActionLoading] = useState(null);
  // Section lives in the URL (?section=...) so Back returns to the tab
  // you were on — see useDashboardSection.
  const [activeSection, setActiveSection] = useDashboardSection('dashboard');
  const [isDarkMode, setIsDarkMode] = useState(() => getStoredDarkMode());
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState('profile');

  // Dynamic state from backend
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState({
    name: 'Loading...',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    userType: 'buyer',
    isVerified: false
  });
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({
    activeOrders: 0,
    completedOrders: 0,
    wishlistItems: 0,
    totalSpent: 0,
    totalSaved: 0,
    exclusiveDeals: 0,
    priorityAccess: 0,
    rewardPoints: 0
  });

  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [priorityListings, setPriorityListings] = useState([]);
  const [badgeProgress, setBadgeProgress] = useState({
    currentLevel: 'Buyer',
    currentPoints: 0,
    nextLevel: 'Bronze Buyer',
    nextLevelPoints: 1000,
    achievements: [],
    perks: []
  });

  // Wallet state
  const [walletBalance, setWalletBalance] = useState({ balance: 0, heldBalance: 0 });
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showBankAccounts, setShowBankAccounts] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [isProcessingTopup, setIsProcessingTopup] = useState(false);

  // Price alerts state
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [newAlertProductId, setNewAlertProductId] = useState('');
  const [newAlertTargetPrice, setNewAlertTargetPrice] = useState('');
  const [creatingAlert, setCreatingAlert] = useState(false);

  // Active sessions state
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [terminatingSession, setTerminatingSession] = useState(null);

  // Bug report state
  const [showBugReportModal, setShowBugReportModal] = useState(false);
  const [bugReportData, setBugReportData] = useState({
    type: '',
    title: '',
    description: '',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: '',
    priority: 'medium',
    screenshots: []
  });
  const [submittingBugReport, setSubmittingBugReport] = useState(false);

  // Helper function to get clean display name (prevents duplicates)
  const getDisplayName = (nameString) => {
    if (!nameString) return 'User';

    // Remove extra spaces and trim
    const cleanName = String(nameString).trim().replace(/\s+/g, ' ');

    // Check if name is duplicated (e.g., "John John" or "cybersage cybersage")
    const words = cleanName.split(' ');
    if (words.length >= 2) {
      // Check if consecutive words are the same
      const uniqueWords = [];
      for (let i = 0; i < words.length; i++) {
        if (i === 0 || words[i].toLowerCase() !== words[i - 1].toLowerCase()) {
          uniqueWords.push(words[i]);
        }
      }
      return uniqueWords.join(' ');
    }

    return cleanName;
  };

  // Settings state
  const [editMode, setEditMode] = useState({
    profile: false,
    password: false,
    phone: false,
    avatar: false
  });
  const [settingsFormData, setSettingsFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    avatarUrl: '',
    avatar: ''
  });
  const [avatarUploadMethod, setAvatarUploadMethod] = useState('url'); // 'url' or 'file'
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarFileInputRef = React.useRef(null);

  const [notificationPrefs, setNotificationPrefs] = useState({
    pushNotifications: true,
    emailNotifications: true,
    smsNotifications: false,
    exclusiveDeals: true,
    priceDrops: true,
    earlyAccess: true,
    orderUpdates: true,
    vipEvents: false,
    marketingEmails: true
  });
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'public',
    showPurchaseHistory: false,
    showWishlist: false,
    dataCollection: true,
    analyticsTracking: true
  });
  const [toast, setToast] = useState(null);
  const [primaryAddress, setPrimaryAddress] = useState(null);

  // Sync address list → primaryAddress state
  const handleAddressChange = (addressList) => {
    const def = addressList.find(a => a.isDefault || a.is_default) || addressList[0] || null;
    setPrimaryAddress(def);
  };

  useEffect(() => {
    document.body.classList.toggle('AutoboyExpress-dash-dark-mode', isDarkMode);
    // Tailwind's compiled `:where(...)` selector for our custom darkMode config
    // only expands `.tw-dark-mode` to also match descendants (`.tw-dark-mode *`);
    // `.tw-AutoboyExpress-dash-dark-mode` compiles as a self-match-only term, so
    // toggling it on <body> never reaches nested `dark:` utilities (see ui/DataTable.jsx).
    // `tw-dark-mode` is the class that actually cascades — ThemeSync strips it on
    // dashboard routes to avoid storefront bleed, so we re-assert it here.
    document.body.classList.toggle('tw-dark-mode', isDarkMode);
    setStoredDarkMode(isDarkMode);
  }, [isDarkMode]);

  // Update settings form data when user profile changes
  useEffect(() => {
    setSettingsFormData({
      firstName: userProfile.firstName || '',
      lastName: userProfile.lastName || '',
      phone: userProfile.phone || '',
      email: userProfile.email || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      avatarUrl: userProfile.avatar || '',
      avatar: userProfile.avatar || ''
    });
    setAvatarPreview(userProfile.avatar || null);
  }, [userProfile]);

  // Sync primaryAddress → location field in settings
  useEffect(() => {
    if (!primaryAddress) return;
    const parts = [primaryAddress.city, primaryAddress.state, primaryAddress.country].filter(Boolean);
    const locationStr = parts.join(', ');
    setSettingsFormData(prev => ({ ...prev, location: locationStr }));
  }, [primaryAddress]);

  // Fetch all buyer dashboard data from backend
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const [
          profileData,
          dashboardData,
          ordersData,
          wishlistData,
          notificationsData,
          priorityListingsResponse,
          buyerAnalytics,
          userBadgesData,
          userRewardsData
        ] = await Promise.all([
          userAPI.getProfile().catch(() => ({ data: null })),
          buyerAPI.getDashboard().catch(() => ({ data: null })),
          ordersAPI.getUserOrders().catch(() => ({ data: [] })),
          userAPI.getWishlist().catch(() => ({ data: [] })),
          notificationsAPI.getNotifications().catch(() => ({ data: [] })),
          // dealsAPI.getPriorityListings is the real, premium-gated feature
          // for this section (403s for non-premium; filters on
          // priority_access/early_access/vip_only) — this used to call the
          // same public productsAPI.getFeatured() the anonymous Homepage
          // shows, so "Priority Listings" was indistinguishable from what
          // any guest sees.
          dealsAPI.getPriorityListings().catch(() => ({ data: null })),
          buyerAPI.getAnalytics().catch(() => ({ data: null })),
          badgesAPI.getUserBadges().catch(() => ({ data: null })),
          badgesAPI.getUserRewards().catch(() => ({ data: null }))
        ]);

        // Update user profile
        // Get user data from localStorage as fallback
        const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');

        // CRITICAL FIX: Always prefer localStorage cache for fields that might be missing from backend
        // This prevents data loss when backend returns incomplete profile data
        const profile = profileData?.data?.profile || {};
        const backendData = profileData?.data || {};



        // MERGE backend and cache - prioritize whichever has the data
        // This ensures we don't lose data when backend returns incomplete info
        const firstName = profile.first_name || profile.firstName || backendData.first_name ||
                         cachedUser.first_name || cachedUser.firstName ||
                         cachedUser.profile?.first_name || cachedUser.profile?.firstName || '';

        const lastName = profile.last_name || profile.lastName || backendData.last_name ||
                        cachedUser.last_name || cachedUser.lastName ||
                        cachedUser.profile?.last_name || cachedUser.profile?.lastName || '';

        const avatarUrl = profile.avatar || backendData.avatar ||
                         cachedUser.avatar || cachedUser.profile?.avatar || '';

        // Build display name: prefer combined firstName + lastName, then username, then name field
        let displayName = '';
        if (firstName || lastName) {
          displayName = `${firstName} ${lastName}`.trim();
        } else {
          displayName = backendData.username || backendData.name || cachedUser.username || cachedUser.name || 'User';
        }


        const mergedProfile = {
          id: backendData.id || backendData._id || cachedUser.id,
          name: displayName,
          username: backendData.username || cachedUser.username || displayName,
          firstName: firstName,
          lastName: lastName,
          email: backendData.email || cachedUser.email || '',
          phone: backendData.phone || cachedUser.phone || '',
          avatar: avatarUrl,
          userType: backendData.user_type || cachedUser.user_type || 'buyer',
          memberSince: backendData.created_at || cachedUser.created_at || new Date().toISOString(),
          isVerified: profile.verification_status === 'verified' || false
        };

        setUserProfile(mergedProfile);

        // Update localStorage with merged data to ensure it's always complete
        const updatedCache = {
          ...cachedUser,
          ...mergedProfile,
          // Also update both naming conventions
          first_name: firstName,
          firstName: firstName,
          last_name: lastName,
          lastName: lastName,
          avatar: avatarUrl,
          profile: {
            ...(cachedUser.profile || {}),
            first_name: firstName,
            lastName: lastName,
            firstName: firstName,
            last_name: lastName,
            avatar: avatarUrl
          }
        };
        localStorage.setItem('user', JSON.stringify(updatedCache));

        // Update dashboard stats
        if (dashboardData?.data) {
          setStats({
            activeOrders: dashboardData.data.active_orders || 0,
            completedOrders: dashboardData.data.completed_orders || 0,
            wishlistItems: dashboardData.data.wishlist_items || 0,
            totalSpent: dashboardData.data.total_spent || 0,
            totalSaved: dashboardData.data.total_saved || 0,
            exclusiveDeals: dashboardData.data.exclusive_deals || 0,
            priorityAccess: dashboardData.data.priority_access || 0,
            rewardPoints: dashboardData.data.reward_points || 0
          });
        }

        // Update orders
        if (ordersData?.data) {
          setOrders(Array.isArray(ordersData.data) ? ordersData.data : []);
        }

        // Update wishlist
        if (wishlistData?.data) {
          setWishlist(Array.isArray(wishlistData.data) ? wishlistData.data : []);
        }

        // Update recent activity from dashboard data
        if (dashboardData?.data?.recent_activity) {
          setRecentActivity(Array.isArray(dashboardData.data.recent_activity) ? dashboardData.data.recent_activity : []);
        } else if (ordersData?.data) {
          // Fallback: Generate recent activity from orders
          const activities = Array.isArray(ordersData.data) ? ordersData.data.slice(0, 5).map(order => ({
            id: order._id,
            type: 'order',
            title: `Order #${order.order_number || order._id?.slice(-6)}`,
            description: `Status: ${order.status}`,
            timestamp: order.created_at || order.createdAt,
            icon: '📦'
          })) : [];
          setRecentActivity(activities);
        }

        // Update notifications
        if (notificationsData?.data) {
          setNotifications(Array.isArray(notificationsData.data) ? notificationsData.data : []);
        }

        // Update priority listings (use featured products)
        if (priorityListingsResponse?.data) {
          // GetPriorityListings returns { products: [...], count } — 403s
          // (caught above into { data: null }) for a non-premium requester,
          // so an empty/missing list here can also legitimately mean no
          // product is currently flagged priority_access/early_access/
          // vip_only, not a fetch failure.
          const products = priorityListingsResponse.data.products || priorityListingsResponse.data;
          setPriorityListings(Array.isArray(products) ? products.slice(0, 5) : []);
        }

        // Update analytics
        if (buyerAnalytics?.data) {
          setAnalyticsData(buyerAnalytics.data);
        }

        // Update badge and rewards data (matching backend: handlers/badges.go)
        if (userRewardsData?.data) {
          const rewardPoints = userRewardsData.data.points || 0;
          const userLevel = userRewardsData.data.level || calculateUserLevel(rewardPoints);

          // Calculate next level and required points (matching backend level system)
          const { nextLevel, nextLevelPoints } = getNextLevelInfo(rewardPoints);

          // Get earned badges
          const earnedBadges = userBadgesData?.data?.badges || [];

          // Extract achievements and perks from badges
          const achievements = earnedBadges.map(badge => ({
            id: badge.badge?.id || badge.id,
            name: badge.badge?.name || 'Achievement',
            description: badge.badge?.description || '',
            earnedAt: badge.earned_at,
            icon: badge.badge?.icon || '🏆'
          }));

          // Define perks based on level
          const perks = getLevelPerks(userLevel);

          setBadgeProgress({
            currentLevel: userLevel,
            currentPoints: rewardPoints,
            nextLevel: nextLevel,
            nextLevelPoints: nextLevelPoints,
            achievements: achievements,
            perks: perks
          });

          // Also update reward points in stats if not already set
          if (dashboardData?.data && !dashboardData.data.reward_points) {
            setStats(prevStats => ({
              ...prevStats,
              rewardPoints: rewardPoints
            }));
          }
        }

      } catch (error) {
        if (error.isAuthError) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [navigate]);

  // Check for payment success on page load - Verify payment then update wallet
  useEffect(() => {
    const verifyAndUpdateWallet = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get('payment');
      const reference = urlParams.get('reference') ||
                       urlParams.get('trxref') ||
                       localStorage.getItem('pending_payment_reference');

      if (paymentStatus === 'success' || reference) {
        try {
          // Show verifying message
          setToast({ message: '🔄 Verifying payment...', type: 'info' });

          // Switch to wallet section
          setActiveSection('wallet');

          // Clean up URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);

          // Verify payment with backend (same as premium upgrade)
          const { paymentAPI } = await import('../services/api');

          const verificationResponse = await paymentAPI.verifyPayment(reference);

          if (verificationResponse && verificationResponse.success) {
            const paymentData = verificationResponse.data;

            // Check if payment is for wallet top-up
            if (paymentData.payment_type === 'wallet_topup') {
              // Fetch updated wallet balance
              const walletResponse = await walletAPI.getBalance();

              const pbVerWalPayload = walletResponse?.data || walletResponse;
              if (pbVerWalPayload) {
                const balanceData = pbVerWalPayload.balance !== undefined ? pbVerWalPayload : (pbVerWalPayload.data || pbVerWalPayload);

                const newBalance = parseFloat(balanceData.balance) || 0;
                const newHeldBalance = parseFloat(balanceData.held_balance || balanceData.heldBalance) || 0;

                setWalletBalance({
                  balance: newBalance,
                  heldBalance: newHeldBalance
                });


                // Show credit notification with amount
                const topupAmount = parseFloat(paymentData.amount) || 0;
                setToast({ message: `💰 Wallet Credited: +₦${topupAmount.toLocaleString()} | New Balance: ₦${newBalance.toLocaleString()}`, type: 'success' });
              } else {
                setToast({ message: '✓ Payment verified but unable to fetch updated balance. Please refresh the page.', type: 'success' });
              }

              // Clear pending payment data
              localStorage.removeItem('pending_payment_reference');
              localStorage.removeItem('pending_payment_id');

              // Switch to wallet section to show updated balance
              setActiveSection('wallet');

              // Refresh transactions after a short delay
              setTimeout(() => {
                fetchWalletTransactions();
              }, 500);
            } else {
              setToast({ message: '✓ Payment verified successfully!', type: 'success' });
            }
          } else {
            setToast({ message: verificationResponse?.message || 'Payment verification failed. Please contact support.', type: 'error' });
          }
        } catch (error) {
          setToast({ message: error.message || 'Error verifying payment. Please contact support.', type: 'info' });
        }
      }
    };

    verifyAndUpdateWallet();
  }, []);

  // Load notification preferences from API
  useEffect(() => {
    const loadNotifPrefs = async () => {
      try {
        const res = await notificationsAPI.getPreferences();
        const data = res?.data || {};
        setNotificationPrefs(prev => ({
          ...prev,
          pushNotifications: data.push_notifications ?? data.pushNotifications ?? prev.pushNotifications,
          emailNotifications: data.email_notifications ?? data.emailNotifications ?? prev.emailNotifications,
          smsNotifications: data.sms_notifications ?? data.smsNotifications ?? prev.smsNotifications,
          exclusiveDeals: data.exclusive_deals ?? data.exclusiveDeals ?? prev.exclusiveDeals,
          priceDrops: data.price_drops ?? data.priceDrops ?? prev.priceDrops,
          earlyAccess: data.early_access ?? data.earlyAccess ?? prev.earlyAccess,
          orderUpdates: data.order_updates ?? data.orderUpdates ?? prev.orderUpdates,
          vipEvents: data.vip_events ?? data.vipEvents ?? prev.vipEvents,
          marketingEmails: data.marketing_emails ?? data.marketingEmails ?? prev.marketingEmails,
        }));
      } catch {
        // keep defaults on error
      }
    };
    loadNotifPrefs();
  }, []);

  const handleSaveNotifPrefs = async () => {
    try {
      setSavingNotifPrefs(true);
      await notificationsAPI.updatePreferences({
        push_notifications: notificationPrefs.pushNotifications,
        email_notifications: notificationPrefs.emailNotifications,
        sms_notifications: notificationPrefs.smsNotifications,
        exclusive_deals: notificationPrefs.exclusiveDeals,
        price_drops: notificationPrefs.priceDrops,
        early_access: notificationPrefs.earlyAccess,
        order_updates: notificationPrefs.orderUpdates,
        vip_events: notificationPrefs.vipEvents,
        marketing_emails: notificationPrefs.marketingEmails,
      });
      setToast({ message: 'Notification preferences saved.', type: 'success' });
    } catch {
      setToast({ message: 'Failed to save notification preferences.', type: 'error' });
    } finally {
      setSavingNotifPrefs(false);
    }
  };

  // Load price alerts from API
  useEffect(() => {
    const loadPriceAlerts = async () => {
      try {
        const res = await priceAlertsAPI.getPriceAlerts();
        setPriceAlerts(Array.isArray(res?.data) ? res.data : []);
      } catch {
        // silently fail — keep empty list
      }
    };
    loadPriceAlerts();
  }, []);

  const handleCreatePriceAlert = async () => {
    if (!newAlertProductId.trim() || !newAlertTargetPrice) return;
    try {
      setCreatingAlert(true);
      await priceAlertsAPI.createPriceAlert(newAlertProductId.trim(), parseFloat(newAlertTargetPrice));
      setToast({ message: 'Price alert created!', type: 'success' });
      setNewAlertProductId('');
      setNewAlertTargetPrice('');
      const res = await priceAlertsAPI.getPriceAlerts();
      setPriceAlerts(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setToast({ message: err?.response?.data?.error || 'Failed to create price alert.', type: 'error' });
    } finally {
      setCreatingAlert(false);
    }
  };

  const handleDeletePriceAlert = async (alertId) => {
    try {
      await priceAlertsAPI.deletePriceAlert(alertId);
      setPriceAlerts(prev => prev.filter(a => (a.id || a._id) !== alertId));
      setToast({ message: 'Price alert removed.', type: 'info' });
    } catch {
      setToast({ message: 'Failed to remove price alert.', type: 'error' });
    }
  };

  // Load active sessions when settings section is active
  useEffect(() => {
    if (activeSection === 'settings') {
      setSessionsLoading(true);
      userAPI.getActiveSessions()
        .then(res => {
          const sessions = res?.data?.sessions || res?.data || [];
          setActiveSessions(Array.isArray(sessions) ? sessions : []);
        })
        .catch(() => setActiveSessions([]))
        .finally(() => setSessionsLoading(false));
    }
  }, [activeSection]);

  const handleTerminateSession = async (sessionId) => {
    try {
      setTerminatingSession(sessionId);
      await userAPI.terminateSession(sessionId);
      setActiveSessions(prev => prev.filter(s => (s.id || s._id) !== sessionId));
      setToast({ message: 'Session terminated.', type: 'success' });
    } catch {
      setToast({ message: 'Failed to terminate session.', type: 'error' });
    } finally {
      setTerminatingSession(null);
    }
  };

  const handleTerminateAllSessions = async () => {
    if (!window.confirm('Sign out of all other devices?')) return;
    try {
      await userAPI.terminateAllOtherSessions();
      const res = await userAPI.getActiveSessions();
      const sessions = res?.data?.sessions || res?.data || [];
      setActiveSessions(Array.isArray(sessions) ? sessions : []);
      setToast({ message: 'All other sessions terminated.', type: 'success' });
    } catch {
      setToast({ message: 'Failed to terminate sessions.', type: 'error' });
    }
  };

  // Fetch wallet balance when wallet section is active
  useEffect(() => {
    if (activeSection === 'wallet') {
      fetchWalletBalance();
      fetchWalletTransactions();
    }
  }, [activeSection]);

  // Auto-refresh wallet balance every 30 seconds when on wallet section
  useEffect(() => {
    let refreshInterval;
    if (activeSection === 'wallet') {
      refreshInterval = setInterval(() => {
        fetchWalletBalance();
        fetchWalletTransactions();
      }, 30000); // Refresh every 30 seconds
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [activeSection]);

  // Fetch wallet balance with optional success notification (matching mobile app)
  const fetchWalletBalance = async (showNotification = false) => {
    try {
      const response = await walletAPI.getBalance();

      const pbWalPayload = response?.data || response;
      if (pbWalPayload) {
        const balanceData = pbWalPayload.balance !== undefined ? pbWalPayload : (pbWalPayload.data || pbWalPayload);

        const availableBalance = parseFloat(balanceData.balance) || 0;
        const heldBalance = parseFloat(
          balanceData.held_balance ||
          balanceData.heldBalance ||
          balanceData.escrow_balance || balanceData.escrowBalance ||
          0
        );
        const pendingBalance = parseFloat(
          balanceData.pending_balance ||
          balanceData.pendingBalance ||
          0
        );

        setWalletBalance({
          balance: availableBalance,
          heldBalance: heldBalance,
          pendingBalance: pendingBalance,
          totalBalance: availableBalance + heldBalance + pendingBalance
        });

        if (showNotification) {
          setToast({ message: `✓ Wallet balance refreshed: ₦${availableBalance.toLocaleString()}`, type: 'success' });
        }
      } else {
        if (showNotification) {
          setToast({ message: 'Unable to fetch wallet balance', type: 'error' });
        }
      }
    } catch (error) {
      if (showNotification) {
        setToast({ message: 'Failed to refresh wallet balance', type: 'error' });
      }
    }
  };

  // Fetch wallet transactions (matching mobile app implementation)
  const fetchWalletTransactions = async () => {
    setIsLoadingTransactions(true);
    try {
      const response = await walletAPI.getTransactions({ limit: 50 });

      const pbTxPayload = response?.data || response;
      if (pbTxPayload) {
        let transactions = [];
        if (Array.isArray(pbTxPayload)) {
          transactions = pbTxPayload;
        } else if (Array.isArray(pbTxPayload.transactions)) {
          transactions = pbTxPayload.transactions;
        } else if (pbTxPayload.data && Array.isArray(pbTxPayload.data)) {
          transactions = pbTxPayload.data;
        }

        transactions.sort((a, b) => {
          const dateA = new Date(a.created_at || a.createdAt).getTime();
          const dateB = new Date(b.created_at || b.createdAt).getTime();
          return dateB - dateA;
        });

        setWalletTransactions(transactions);
      } else {
        setWalletTransactions([]);
      }
    } catch (error) {
      setWalletTransactions([]); // Set empty array on error
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('userType');
      navigate('/auth');
    }
  };

  const handleWalletTopup = async () => {
    const amount = parseFloat(topupAmount);

    if (!amount || amount < 100) {
      setToast({ message: 'Minimum top-up amount is ₦100', type: 'warning' });
      return;
    }

    try {
      setIsProcessingTopup(true);

      const { paymentAPI } = await import('../services/api');

      const paymentData = {
        amount: parseInt(amount),
        payment_type: 'wallet_topup',
        currency: 'NGN',
        callback_url: `${window.location.origin}/premium-buyer-dashboard?payment=success`,
        metadata: {
          topup_amount: parseInt(amount)
        }
      };

      const paymentResponse = await paymentAPI.initializePayment(paymentData);

      if (paymentResponse && paymentResponse.data && paymentResponse.data.checkout_url) {
        localStorage.setItem('pending_payment_id', paymentResponse.data.payment_id);
        localStorage.setItem('pending_payment_reference', paymentResponse.data.reference);
        window.location.href = paymentResponse.data.checkout_url;
      } else {
        setToast({ message: 'Failed to initialize payment', type: 'error' });
      }
    } catch (error) {
      setToast({ message: error.message || 'Failed to process top-up', type: 'error' });
    } finally {
      setIsProcessingTopup(false);
    }
  };

  // Handle bug report submission
  const handleSubmitBugReport = async () => {
    if (!bugReportData.type || !bugReportData.title || !bugReportData.description) {
      setToast({ message: 'Please fill in all required fields (Bug Type, Title, and Description)', type: 'warning' });
      return;
    }

    try {
      setSubmittingBugReport(true);
      await reportsAPI.submitBugReport(bugReportData);

      setToast({ message: 'Bug report submitted successfully! Thank you for helping us improve AutoboyExpress. We\'ll investigate this issue and get back to you soon.', type: 'success' });

      // Reset form and close modal
      setBugReportData({
        type: '',
        title: '',
        description: '',
        stepsToReproduce: '',
        expectedBehavior: '',
        actualBehavior: '',
        priority: 'medium',
        screenshots: []
      });
      setShowBugReportModal(false);
    } catch (error) {
      setToast({ message: error.message || 'Failed to submit bug report. Please try again.', type: 'error' });
    } finally {
      setSubmittingBugReport(false);
    }
  };

  const toggleTheme = () => setIsDarkMode(!isDarkMode);
  const toggleMobileSidebar = () => setIsMobileSidebarOpen(!isMobileSidebarOpen);
  const formatCurrency = (amount) => `₦${amount.toLocaleString()}`;

  // Dark mode color helper
  const getColor = (lightColor, darkColor) => isDarkMode ? darkColor : lightColor;

  // Format date
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateString;
    }
  };


  // Chart Data with null safety
  const spendingChartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [{
      label: 'Monthly Spending',
      data: analyticsData?.monthlySpending || [0, 0, 0, 0, 0, 0],
      borderColor: '#22C55E',
      backgroundColor: 'rgba(34, 197, 94, 0.1)',
      tension: 0.4
    }]
  };

  const categoryChartData = {
    labels: analyticsData?.categoryBreakdown ? Object.keys(analyticsData.categoryBreakdown) : ['No Data'],
    datasets: [{
      data: analyticsData?.categoryBreakdown ? Object.values(analyticsData.categoryBreakdown) : [1],
      backgroundColor: ['#22C55E', '#3B82F6', '#F59E0B', '#EF4444'],
      borderWidth: 0
    }]
  };

  const savingsChartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [{
      label: 'Total Savings',
      data: analyticsData?.savingsTimeline || [0, 0, 0, 0, 0, 0],
      backgroundColor: 'rgba(34, 197, 94, 0.8)',
      borderColor: '#22C55E',
      borderWidth: 1
    }]
  };

  // Helper function to get proper image URL
  const getImageUrl = (item) => {
    // Handle different image field formats from API
    if (item.images && Array.isArray(item.images) && item.images.length > 0) {
      // If images is an array of objects with url property
      const firstImage = item.images[0];
      return firstImage.url || firstImage.path || firstImage;
    } else if (item.image_url) {
      return item.image_url;
    } else if (item.image) {
      return item.image;
    } else if (item.thumbnail) {
      return item.thumbnail;
    }
    // Fallback: Use CSS gradient background instead of external placeholder
    return null;
  };

  // Dashboard Content
  const renderDashboardContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      {/* KYC Status Banner */}
      <KYCStatusBanner onNavigateToKYC={() => setActiveSection('kyc')} />

      {/* Premium Welcome Banner with Glass Morphism */}
      <div style={{
        background: getColor(
          'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.15) 100%)',
          'linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(16, 185, 129, 0.08) 100%)'
        ),
        backdropFilter: 'blur(20px)',
        border: getColor('1px solid rgba(34, 197, 94, 0.2)', '1px solid rgba(34, 197, 94, 0.15)'),
        borderRadius: '24px',
        padding: '2rem',
        marginBottom: '2rem',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: getColor('0 8px 32px rgba(0, 0, 0, 0.1)', '0 8px 32px rgba(0, 0, 0, 0.3)')
      }}>
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '400px',
          height: '400px',
          background: getColor(
            'radial-gradient(circle, rgba(34, 197, 94, 0.15) 0%, transparent 70%)',
            'radial-gradient(circle, rgba(34, 197, 94, 0.08) 0%, transparent 70%)'
          ),
          borderRadius: '50%',
          filter: 'blur(60px)'
        }} />
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1.5rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
              <div style={{
                width: '56px',
                height: '56px',
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(255, 215, 0, 0.4)',
                animation: 'pulse 2s ease-in-out infinite'
              }}>
                <FontAwesomeIcon icon={faCrown} style={{ fontSize: '28px', color: '#000' }} />
              </div>
              <div>
                <h2 style={{
                  fontSize: '1.75rem',
                  fontWeight: '700',
                  marginBottom: '0.25rem',
                  background: getColor(
                    'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
                    'linear-gradient(135deg, #E5E7EB 0%, #F9FAFB 100%)'
                  ),
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Welcome back, {userProfile.firstName || userProfile.name}!
                </h2>
                <p style={{ fontSize: '0.95rem', color: getColor('#6B7280', '#9CA3AF'), fontWeight: '500' }}>
                  <FontAwesomeIcon icon={faTrophy} style={{ marginRight: '0.5rem', color: '#FFD700' }} />
                  {badgeProgress.currentLevel} • {badgeProgress.currentPoints} Points
                  <span style={{ margin: '0 0.5rem', color: getColor('#D1D5DB', '#4B5563') }}>•</span>
                  {badgeProgress.nextLevelPoints - badgeProgress.currentPoints} to {badgeProgress.nextLevel}
                </p>
              </div>
            </div>
            <div style={{
              background: getColor('rgba(34, 197, 94, 0.1)', 'rgba(34, 197, 94, 0.15)'),
              border: getColor('1px solid rgba(34, 197, 94, 0.2)', '1px solid rgba(34, 197, 94, 0.25)'),
              borderRadius: '12px',
              padding: '0.75rem 1rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginTop: '0.5rem'
            }}>
              <FontAwesomeIcon icon={faFire} style={{ color: '#FF6B35', fontSize: '18px' }} />
              <span style={{ fontSize: '0.9rem', fontWeight: '600', color: getColor('#1F2937', '#F9FAFB') }}>
                {stats.exclusiveDeals} Exclusive Deals Available
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setActiveSection('exclusive-deals')}
              style={{
                background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '0.875rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(34, 197, 94, 0.3)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 24px rgba(34, 197, 94, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 16px rgba(34, 197, 94, 0.3)';
              }}
            >
              <FontAwesomeIcon icon={faGift} />
              View Deals
            </button>
            <button
              onClick={() => navigate('/shop')}
              style={{
                background: getColor('rgba(255, 255, 255, 0.9)', 'rgba(20, 20, 20, 0.95)'),
                color: getColor('#1F2937', '#F9FAFB'),
                border: getColor('1px solid rgba(209, 213, 219, 0.5)', '1px solid rgba(75, 85, 99, 0.4)'),
                borderRadius: '12px',
                padding: '0.875rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <FontAwesomeIcon icon={faShoppingBag} />
              Browse Shop
            </button>
          </div>
        </div>
      </div>

      {/* Modern Stats Cards with Gradient Borders */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.25rem',
        marginBottom: '2rem'
      }}>
        {[
          { icon: faShoppingCart, value: stats.activeOrders, label: 'Active Orders', gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)', color: '#3B82F6' },
          { icon: faCheckCircle, value: stats.completedOrders, label: 'Completed Orders', gradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#22C55E' },
          { icon: faNairaSign, value: formatCurrency(stats.totalSaved), label: 'Total Savings', gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', color: '#F59E0B' },
          { icon: faGift, value: stats.rewardPoints, label: 'Reward Points', gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', color: '#8B5CF6' }
        ].map((stat, idx) => (
          <div
            key={idx}
            style={{
              background: getColor('rgba(255, 255, 255, 0.8)', 'rgba(20, 20, 20, 0.95)'),
              backdropFilter: 'blur(20px)',
              border: getColor('1px solid rgba(209, 213, 219, 0.3)', '1px solid rgba(60, 60, 60, 0.5)'),
              borderRadius: '20px',
              padding: '1.75rem',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              boxShadow: getColor('0 4px 12px rgba(0, 0, 0, 0.05)', '0 4px 12px rgba(0, 0, 0, 0.5)')
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = isDarkMode ? '0 12px 32px rgba(0, 0, 0, 0.5)' : '0 12px 32px rgba(0, 0, 0, 0.12)';
              e.currentTarget.style.borderColor = stat.color;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.05)';
              e.currentTarget.style.borderColor = isDarkMode ? 'rgba(60, 60, 60, 0.5)' : 'rgba(209, 213, 219, 0.3)';
            }}
          >
            <div style={{
              position: 'absolute',
              top: '-30%',
              right: '-20%',
              width: '120px',
              height: '120px',
              background: stat.gradient,
              borderRadius: '50%',
              opacity: 0.1,
              filter: 'blur(40px)'
            }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: stat.gradient,
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1rem',
                boxShadow: `0 4px 16px ${stat.color}40`
              }}>
                <FontAwesomeIcon icon={stat.icon} style={{ fontSize: '20px', color: 'white' }} />
              </div>
              <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.25rem', color: getColor('#1F2937', '#F9FAFB') }}>
                {stat.value}
              </div>
              <div style={{ fontSize: '0.9rem', color: getColor('#6B7280', '#9CA3AF'), fontWeight: '500' }}>
                {stat.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 500px), 1fr))', gap: '1.5rem' }}>
        {/* Priority Listings - Enhanced with better image handling */}
        <div style={{
          background: getColor('rgba(255, 255, 255, 0.9)', 'rgba(18, 18, 18, 0.98)'),
          backdropFilter: 'blur(20px)',
          border: getColor('1px solid rgba(209, 213, 219, 0.3)', '1px solid rgba(50, 50, 50, 0.6)'),
          borderRadius: '20px',
          padding: '1.75rem',
          boxShadow: getColor('0 4px 12px rgba(0, 0, 0, 0.05)', '0 4px 12px rgba(0, 0, 0, 0.6)')
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FontAwesomeIcon icon={faBolt} style={{ color: 'white', fontSize: '18px' }} />
              </div>
              <h2 style={{ fontSize: '1.35rem', fontWeight: '700', color: getColor('#1F2937', '#F9FAFB'), margin: 0 }}>
                Priority Listings
              </h2>
            </div>
            <button
              onClick={() => setActiveSection('priority-listings')}
              style={{
                background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                padding: '0.625rem 1.25rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.25)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 4px 16px rgba(34, 197, 94, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 2px 8px rgba(34, 197, 94, 0.25)';
              }}
            >
              View All
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {priorityListings.length > 0 ? (
              priorityListings.slice(0, 3).map(item => {
                const imageUrl = getImageUrl(item);
                return (
                  <div
                    key={item._id || item.id}
                    onClick={() => navigate(`/product/${item._id || item.id}`)}
                    style={{
                      display: 'flex',
                      gap: '1rem',
                      background: getColor('rgba(249, 250, 251, 0.8)', 'rgba(30, 30, 30, 0.9)'),
                      borderRadius: '16px',
                      padding: '1rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      border: getColor('1px solid rgba(229, 231, 235, 0.5)', '1px solid rgba(60, 60, 60, 0.6)'),
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateX(4px)';
                      e.currentTarget.style.boxShadow = isDarkMode ? '0 8px 24px rgba(0, 0, 0, 0.4)' : '0 8px 24px rgba(0, 0, 0, 0.1)';
                      e.currentTarget.style.borderColor = '#22C55E';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateX(0)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = isDarkMode ? 'rgba(60, 60, 60, 0.6)' : 'rgba(229, 231, 235, 0.5)';
                    }}
                  >
                    {item.badge && (
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                        color: 'white',
                        padding: '0.375rem 0.75rem',
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)'
                      }}>
                        {item.badge.replace('-', ' ')}
                      </div>
                    )}
                    <div style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      flexShrink: 0,
                      background: getColor(
                        'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
                        'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)'
                      ),
                      position: 'relative'
                    }}>
                      <img
                        src={imageUrl}
                        alt={item.name || item.title || 'Product'}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <div>
                        <h4 style={{
                          fontSize: '1.05rem',
                          fontWeight: '600',
                          color: getColor('#1F2937', '#F9FAFB'),
                          marginBottom: '0.375rem',
                          lineHeight: '1.4'
                        }}>
                          {item.name || item.title || 'Premium Product'}
                        </h4>
                        <p style={{
                          fontSize: '0.875rem',
                          color: getColor('#6B7280', '#9CA3AF'),
                          marginBottom: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.375rem'
                        }}>
                          <FontAwesomeIcon icon={faStore} style={{ fontSize: '0.8rem' }} />
                          {item.seller_premium
                            ? (item.seller || item.seller_name || 'AutoboyExpress Seller')
                            : `Seller ${String(item.seller_id || '000').slice(-3).padStart(3, '0')}`}
                        </p>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          {item.originalPrice && (
                            <span style={{
                              fontSize: '0.875rem',
                              color: getColor('#9CA3AF', '#6B7280'),
                              textDecoration: 'line-through',
                              marginRight: '0.5rem'
                            }}>
                              {formatCurrency(item.originalPrice)}
                            </span>
                          )}
                          <span style={{
                            fontSize: '1.25rem',
                            fontWeight: '700',
                            background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                          }}>
                            {formatCurrency(item.price || 0)}
                          </span>
                        </div>
                        {item.timeLeft && (
                          <span style={{
                            fontSize: '0.8rem',
                            color: '#EF4444',
                            background: 'rgba(239, 68, 68, 0.1)',
                            padding: '0.375rem 0.625rem',
                            borderRadius: '8px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.375rem'
                          }}>
                            <FontAwesomeIcon icon={faClock} />
                            {item.timeLeft}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: getColor(
                    'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
                    'linear-gradient(135deg, #374151 0%, #4B5563 100%)'
                  ),
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <FontAwesomeIcon icon={faBolt} style={{ fontSize: '32px', color: getColor('#9CA3AF', '#6B7280') }} />
                </div>
                <p style={{ fontSize: '1rem', color: getColor('#6B7280', '#9CA3AF'), fontWeight: '500', marginBottom: '0.5rem' }}>
                  No priority listings yet
                </p>
                <p style={{ fontSize: '0.875rem', color: getColor('#9CA3AF', '#6B7280') }}>
                  Check back soon for exclusive deals
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions - Modern Card Grid */}
        <div style={{
          background: getColor('rgba(255, 255, 255, 0.9)', 'rgba(18, 18, 18, 0.98)'),
          backdropFilter: 'blur(20px)',
          border: getColor('1px solid rgba(209, 213, 219, 0.3)', '1px solid rgba(50, 50, 50, 0.6)'),
          borderRadius: '20px',
          padding: '1.75rem',
          boxShadow: getColor('0 4px 12px rgba(0, 0, 0, 0.05)', '0 4px 12px rgba(0, 0, 0, 0.6)')
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FontAwesomeIcon icon={faBolt} style={{ color: 'white', fontSize: '18px' }} />
            </div>
            <h2 style={{ fontSize: '1.35rem', fontWeight: '700', color: getColor('#1F2937', '#F9FAFB'), margin: 0 }}>
              Quick Actions
            </h2>
          </div>

          <div className="AutoboyExpress-premium-quick-actions-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
            {[
              { icon: faFire, label: 'Exclusive Deals', section: 'exclusive-deals', gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)', color: '#EF4444' },
              { icon: faChartPie, label: 'Analytics', section: 'analytics', gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', color: '#8B5CF6' },
              { icon: faWallet, label: 'My Wallet', section: 'wallet', gradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#22C55E' },
              { icon: faHeart, label: 'Wishlist', section: 'wishlist', gradient: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)', color: '#EC4899' },
              { icon: faChartLine, label: 'Reports', section: 'reports', gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', color: '#F59E0B' }
            ].map((action, idx) => (
              <button
                key={idx}
                onClick={() => setActiveSection(action.section)}
                style={{
                  background: getColor('rgba(249, 250, 251, 0.8)', 'rgba(30, 30, 30, 0.9)'),
                  border: getColor('1px solid rgba(229, 231, 235, 0.5)', '1px solid rgba(60, 60, 60, 0.6)'),
                  borderRadius: '16px',
                  padding: '1.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = `0 12px 32px ${action.color}25`;
                  e.currentTarget.style.borderColor = action.color;
                  e.currentTarget.style.background = `${action.color}08`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = isDarkMode ? 'rgba(75, 85, 99, 0.4)' : 'rgba(229, 231, 235, 0.5)';
                  e.currentTarget.style.background = isDarkMode ? 'rgba(30, 30, 30, 0.9)' : 'rgba(249, 250, 251, 0.8)';
                }}
              >
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: action.gradient,
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 0.875rem',
                  boxShadow: `0 4px 16px ${action.color}40`
                }}>
                  <FontAwesomeIcon icon={action.icon} style={{ fontSize: '24px', color: 'white' }} />
                </div>
                <span style={{
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: getColor('#1F2937', '#F9FAFB'),
                  display: 'block'
                }}>
                  {action.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity Timeline */}
      {recentActivity.length > 0 && (
        <div style={{
          background: getColor('rgba(255, 255, 255, 0.9)', 'rgba(31, 41, 55, 0.9)'),
          backdropFilter: 'blur(20px)',
          border: getColor('1px solid rgba(209, 213, 219, 0.3)', '1px solid rgba(75, 85, 99, 0.3)'),
          borderRadius: '20px',
          padding: '1.75rem',
          marginTop: '1.5rem',
          boxShadow: getColor('0 4px 12px rgba(0, 0, 0, 0.05)', '0 4px 12px rgba(0, 0, 0, 0.3)')
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FontAwesomeIcon icon={faHistory} style={{ color: 'white', fontSize: '18px' }} />
            </div>
            <h2 style={{ fontSize: '1.35rem', fontWeight: '700', color: getColor('#1F2937', '#F9FAFB'), margin: 0 }}>
              Recent Activity
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {recentActivity.slice(0, 5).map((activity, idx) => (
              <div
                key={activity.id || idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem',
                  background: getColor('rgba(249, 250, 251, 0.5)', 'rgba(25, 25, 25, 0.85)'),
                  borderRadius: '12px',
                  border: getColor('1px solid rgba(229, 231, 235, 0.3)', '1px solid rgba(55, 55, 55, 0.5)'),
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDarkMode ? 'rgba(35, 35, 35, 1)' : 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.boxShadow = isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isDarkMode ? 'rgba(25, 25, 25, 0.85)' : 'rgba(249, 250, 251, 0.5)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontSize: '1.5rem' }}>{activity.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.95rem', fontWeight: '600', color: getColor('#1F2937', '#F9FAFB'), marginBottom: '0.125rem' }}>
                    {activity.title}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: getColor('#6B7280', '#9CA3AF') }}>
                    {activity.description}
                  </div>
                </div>
                <div style={{ fontSize: '0.8rem', color: getColor('#9CA3AF', '#6B7280'), whiteSpace: 'nowrap' }}>
                  {activity.timestamp ? formatDate(activity.timestamp) : 'Recently'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Analytics Content
  const renderAnalyticsContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-content">
        <div className="AutoboyExpress-dash-section">
          <div className="AutoboyExpress-dash-section-header"><h2 className="AutoboyExpress-dash-section-title">Monthly Spending</h2></div>
          <div className="AutoboyExpress-dash-section-content">
            <div className="AutoboyExpress-dash-chart-container">
              <Line data={spendingChartData} options={{ responsive: true, maintainAspectRatio: false }} />
            </div>
          </div>
        </div>

        <div className="AutoboyExpress-dash-section">
          <div className="AutoboyExpress-dash-section-header"><h2 className="AutoboyExpress-dash-section-title">Category Breakdown</h2></div>
          <div className="AutoboyExpress-dash-section-content">
            <div className="AutoboyExpress-dash-chart-container">
              <Doughnut data={categoryChartData} options={{ responsive: true, maintainAspectRatio: false }} />
            </div>
          </div>
        </div>

        <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
          <div className="AutoboyExpress-dash-section-header"><h2 className="AutoboyExpress-dash-section-title">Savings Over Time</h2></div>
          <div className="AutoboyExpress-dash-section-content">
            <div className="AutoboyExpress-dash-chart-container">
              <Bar data={savingsChartData} options={{ responsive: true, maintainAspectRatio: false }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Priority Listings Content
  const renderPriorityListingsContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">Priority & Early Access Listings</h2>
          <div className="AutoboyExpress-dash-filters">
            <button className="AutoboyExpress-dash-filter-btn"><FontAwesomeIcon icon={faFilter} /> Filter</button>
          </div>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          <div className="AutoboyExpress-premium-listings-grid">
            {priorityListings.length > 0 ? (
              priorityListings.map(item => (
                <div key={item._id || item.id} className="AutoboyExpress-premium-listing-card">
                  {item.badge && <span className="AutoboyExpress-premium-badge-corner">{item.badge.replace('-', ' ')}</span>}
                  <img
                    src={getImageUrl(item) || 'https://via.placeholder.com/300x300?text=No+Image'}
                    alt={item.name || 'Product'}
                    onError={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)';
                      e.target.style.display = 'flex';
                      e.target.style.alignItems = 'center';
                      e.target.style.justifyContent = 'center';
                    }}
                  />
                  <div className="AutoboyExpress-premium-listing-details">
                    <h4>{item.name || 'Product Name'}</h4>
                    <p>{item.seller_premium
                      ? (item.seller || item.seller_name || 'Seller')
                      : `Seller ${String(item.seller_id || '000').slice(-3).padStart(3, '0')}`}</p>
                    <div className="AutoboyExpress-premium-price-section">
                      <div>
                        {item.originalPrice && <span className="AutoboyExpress-premium-original-price">{formatCurrency(item.originalPrice)}</span>}
                        <strong>{formatCurrency(item.price || 0)}</strong>
                        {item.discount && <span className="AutoboyExpress-premium-discount">Save {formatCurrency(item.discount)}</span>}
                      </div>
                      {item.timeLeft && <span className="AutoboyExpress-premium-time-left"><FontAwesomeIcon icon={faClock} /> {item.timeLeft} left</span>}
                    </div>
                    <button
                      className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                      onClick={() => navigate(`/product/${item._id || item.id}`)}
                    >
                      View
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p style={{ textAlign: 'center', color: '#999', padding: '3rem', gridColumn: '1 / -1' }}>
                No priority listings available at the moment
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Exclusive Deals Content
  const renderExclusiveDealsContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">Exclusive Premium Deals</h2>
          <p style={{ fontSize: '0.85rem', opacity: 0.7, margin: '0.25rem 0 0' }}>
            As a premium member, you see new flash deals as soon as they're posted — everyone else waits.
          </p>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          <FlashDealsStrip isDarkMode={isDarkMode} />
        </div>
      </div>
    </div>
  );

  // Reports Content
  const renderReportsContent = () => {
    const bugTypes = [
      { id: 'crash', name: 'App Crash', icon: faExclamationTriangle },
      { id: 'ui', name: 'UI/UX Issue', icon: faEye },
      { id: 'performance', name: 'Performance', icon: faTachometerAlt },
      { id: 'feature', name: 'Feature Not Working', icon: faCog },
      { id: 'payment', name: 'Payment Issue', icon: faUniversity },
      { id: 'other', name: 'Other', icon: faQuestionCircle },
    ];

    const priorities = [
      { id: 'low', name: 'Low', color: '#4CAF50' },
      { id: 'medium', name: 'Medium', color: '#FF9800' },
      { id: 'high', name: 'High', color: '#FF5722' },
      { id: 'critical', name: 'Critical', color: '#F44336' },
    ];

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        <div className="AutoboyExpress-premium-reports-grid">
          <div className="AutoboyExpress-premium-report-card">
            <h3>Total Spending</h3>
            <div className="AutoboyExpress-premium-report-value">{formatCurrency(stats.totalSpent)}</div>
            <p>Across {stats.completedOrders} orders</p>
          </div>
          <div className="AutoboyExpress-premium-report-card">
            <h3>Total Savings</h3>
            <div className="AutoboyExpress-premium-report-value AutoboyExpress-premium-savings">{formatCurrency(stats.totalSaved)}</div>
            <p>From premium deals</p>
          </div>
          <div className="AutoboyExpress-premium-report-card">
            <h3>Average Order</h3>
            <div className="AutoboyExpress-premium-report-value">{formatCurrency(stats.completedOrders > 0 ? Math.floor(stats.totalSpent / stats.completedOrders) : 0)}</div>
            <p>Per transaction</p>
          </div>
          <div className="AutoboyExpress-premium-report-card">
            <h3>Reward Points</h3>
            <div className="AutoboyExpress-premium-report-value">{stats.rewardPoints}</div>
            <p>Available to redeem</p>
          </div>
        </div>

        <div className="AutoboyExpress-dash-section" style={{ marginTop: '2rem' }}>
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">
              <FontAwesomeIcon icon={faBell} style={{ marginRight: '0.5rem', color: '#22C55E' }} />
              Price Alerts
            </h2>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            {/* Create alert form */}
            <div style={{
              display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem',
              padding: '1rem', position: 'relative',
              background: isDarkMode ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)',
              borderRadius: '10px',
              border: '1px solid rgba(34,197,94,0.2)'
            }}>
              <input
                type="text"
                placeholder="Product ID"
                value={newAlertProductId}
                onChange={e => setNewAlertProductId(e.target.value)}
                className="bd-settings-input"
                style={{ flex: '1 1 200px', width: 'auto' }}
              />
              <input
                type="number"
                placeholder="Target price (₦)"
                value={newAlertTargetPrice}
                onChange={e => setNewAlertTargetPrice(e.target.value)}
                min="0"
                className="bd-settings-input"
                style={{ flex: '1 1 150px', width: 'auto' }}
              />
              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                onClick={handleCreatePriceAlert}
                disabled={creatingAlert || !newAlertProductId.trim() || !newAlertTargetPrice}
                style={{ whiteSpace: 'nowrap' }}
              >
                {creatingAlert ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faBell} />}
                {' '}Set Alert
              </button>
            </div>
            {/* Alert list */}
            {priceAlerts.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {priceAlerts.map(alert => {
                  const alertId = alert.id || alert._id;
                  const productTitle = alert.product?.title || alert.product_name || `Product #${alertId}`;
                  const productImage = alert.product?.image;
                  const isTriggered = alert.status === 'triggered';
                  const isExpired = alert.status === 'expired';
                  const statusColor = isTriggered ? '#22c55e' : isExpired ? '#9ca3af' : '#f59e0b';
                  const statusLabel = isTriggered ? '✓ Target Reached' : isExpired ? 'Expired' : 'Watching';
                  return (
                    <div key={alertId} style={{
                      display: 'flex', alignItems: 'center', gap: '0.9rem',
                      padding: '1rem', borderRadius: '12px',
                      background: isDarkMode ? '#1F2937' : '#F9FAFB',
                      border: `1px solid ${isDarkMode ? '#374151' : '#E5E7EB'}`
                    }}>
                      {productImage && (
                        <img src={productImage} alt={productTitle} style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.2rem', color: isDarkMode ? '#F9FAFB' : '#1F2937' }}>{productTitle}</p>
                        <p style={{ fontSize: '0.8rem', color: isDarkMode ? '#9CA3AF' : '#6b7280' }}>
                          Current: ₦{Number(alert.current_price || 0).toLocaleString()} &bull; Target: ₦{Number(alert.target_price || 0).toLocaleString()}
                        </p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                        <span style={{ fontSize: '0.78rem', color: statusColor, fontWeight: 600, whiteSpace: 'nowrap' }}>{statusLabel}</span>
                        <button
                          onClick={() => handleDeletePriceAlert(alertId)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.3rem', display: 'flex', alignItems: 'center' }}
                          title="Remove alert"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                <FontAwesomeIcon icon={faBell} size="3x" style={{ color: '#22C55E', marginBottom: '1rem', opacity: 0.4 }} />
                <p style={{ fontWeight: 600, color: isDarkMode ? '#F9FAFB' : '#1F2937', marginBottom: '0.5rem' }}>No price alerts yet</p>
                <p style={{ fontSize: '0.875rem', color: isDarkMode ? '#9CA3AF' : '#6b7280' }}>Enter a product ID and target price above to get notified when the price drops.</p>
              </div>
            )}
          </div>
        </div>

        {/* Help & Support Section */}
        <div className="AutoboyExpress-dash-section" style={{ marginTop: '2rem' }}>
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Help & Support</h2>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <button
                onClick={() => setShowBugReportModal(true)}
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                style={{
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  justifyContent: 'center'
                }}
              >
                <FontAwesomeIcon icon={faExclamationTriangle} />
                Report a Bug
              </button>
              <button
                onClick={() => navigate('/help')}
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-secondary"
                style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}
              >
                <FontAwesomeIcon icon={faQuestionCircle} />
                Help Center
              </button>
            </div>
          </div>
        </div>

        {/* Bug Report Modal */}
        {showBugReportModal && (
          <div className="modal-overlay" onClick={() => setShowBugReportModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div className="modal-header">
                <h3>Report a Bug</h3>
                <button className="modal-close" onClick={() => setShowBugReportModal(false)}>×</button>
              </div>

              <div className="modal-body">
                {/* Bug Type Selection */}
                <div className="form-group">
                  <label>Bug Type *</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginTop: '0.5rem' }}>
                    {bugTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setBugReportData({ ...bugReportData, type: type.id })}
                        style={{
                          padding: '0.75rem',
                          border: `2px solid ${bugReportData.type === type.id ? '#22C55E' : '#ddd'}`,
                          borderRadius: '8px',
                          background: bugReportData.type === type.id ? '#22C55E15' : '#f9f9f9',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <FontAwesomeIcon icon={type.icon} color={bugReportData.type === type.id ? '#22C55E' : '#666'} />
                        <span style={{ fontWeight: bugReportData.type === type.id ? '600' : '400', color: bugReportData.type === type.id ? '#22C55E' : '#333' }}>
                          {type.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div className="form-group">
                  <label>Bug Title *</label>
                  <input
                    type="text"
                    value={bugReportData.title}
                    onChange={(e) => setBugReportData({ ...bugReportData, title: e.target.value })}
                    placeholder="Brief description of the bug"
                    maxLength={100}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem' }}
                  />
                </div>

                {/* Description */}
                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    value={bugReportData.description}
                    onChange={(e) => setBugReportData({ ...bugReportData, description: e.target.value })}
                    placeholder="Detailed description of what went wrong"
                    rows="4"
                    maxLength={500}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', resize: 'vertical' }}
                  />
                </div>

                {/* Steps to Reproduce */}
                <div className="form-group">
                  <label>Steps to Reproduce</label>
                  <textarea
                    value={bugReportData.stepsToReproduce}
                    onChange={(e) => setBugReportData({ ...bugReportData, stepsToReproduce: e.target.value })}
                    placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                    rows="3"
                    maxLength={500}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', resize: 'vertical' }}
                  />
                </div>

                {/* Expected vs Actual Behavior */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Expected Behavior</label>
                    <textarea
                      value={bugReportData.expectedBehavior}
                      onChange={(e) => setBugReportData({ ...bugReportData, expectedBehavior: e.target.value })}
                      placeholder="What should happen?"
                      rows="3"
                      maxLength={300}
                      style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', resize: 'vertical' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Actual Behavior</label>
                    <textarea
                      value={bugReportData.actualBehavior}
                      onChange={(e) => setBugReportData({ ...bugReportData, actualBehavior: e.target.value })}
                      placeholder="What actually happened?"
                      rows="3"
                      maxLength={300}
                      style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem', resize: 'vertical' }}
                    />
                  </div>
                </div>

                {/* Priority */}
                <div className="form-group">
                  <label>Priority</label>
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                    {priorities.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setBugReportData({ ...bugReportData, priority: p.id })}
                        style={{
                          padding: '0.5rem 1rem',
                          border: `2px solid ${bugReportData.priority === p.id ? p.color : '#ddd'}`,
                          borderRadius: '20px',
                          background: bugReportData.priority === p.id ? `${p.color}15` : '#fff',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: p.color }} />
                        <span style={{ fontWeight: bugReportData.priority === p.id ? '600' : '400', color: bugReportData.priority === p.id ? p.color : '#333' }}>
                          {p.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn-cancel"
                  onClick={() => setShowBugReportModal(false)}
                  disabled={submittingBugReport}
                >
                  Cancel
                </button>
                <button
                  className="btn-submit-refund"
                  onClick={handleSubmitBugReport}
                  disabled={submittingBugReport || !bugReportData.type || !bugReportData.title || !bugReportData.description}
                  style={{
                    background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                    opacity: (submittingBugReport || !bugReportData.type || !bugReportData.title || !bugReportData.description) ? 0.6 : 1
                  }}
                >
                  {submittingBugReport ? 'Submitting...' : 'Submit Bug Report'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Badge Content
  const renderBadgeContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-premium-badge-overview">
        <div className="AutoboyExpress-premium-badge-main">
          <div className="AutoboyExpress-premium-badge-icon">
            <FontAwesomeIcon icon={faCrown} />
          </div>
          <h2>{badgeProgress.currentLevel}</h2>
          <div className="AutoboyExpress-premium-badge-progress">
            <div className="AutoboyExpress-premium-badge-progress-bar">
              <div className="AutoboyExpress-premium-badge-progress-fill" style={{ width: `${(badgeProgress.currentPoints / badgeProgress.nextLevelPoints) * 100}%` }}></div>
            </div>
            <p>{badgeProgress.currentPoints} / {badgeProgress.nextLevelPoints} points to {badgeProgress.nextLevel}</p>
          </div>
        </div>

        <div className="AutoboyExpress-premium-perks-list">
          <h3>Your Premium Perks</h3>
          {badgeProgress.perks.map((perk, idx) => (
            <div key={idx} className="AutoboyExpress-premium-perk-item">
              <span style={{ fontSize: '1.2rem', marginRight: '0.5rem' }}>{perk.icon || '✓'}</span>
              <div>
                <strong>{perk.title || perk}</strong>
                {perk.description && <p style={{ fontSize: '0.85rem', opacity: 0.8, margin: '0.2rem 0 0 0' }}>{perk.description}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="AutoboyExpress-dash-section" style={{ marginTop: '2rem' }}>
        <div className="AutoboyExpress-dash-section-header"><h2 className="AutoboyExpress-dash-section-title">Achievements</h2></div>
        <div className="AutoboyExpress-dash-section-content">
          <div className="AutoboyExpress-premium-achievements-grid">
            {badgeProgress.achievements.length > 0 ? (
              badgeProgress.achievements.map((achievement, idx) => (
                <div key={achievement.id || idx} className="AutoboyExpress-premium-achievement-card earned">
                  <span style={{ fontSize: '2rem' }}>{achievement.icon || '🏆'}</span>
                  <h4>{achievement.name}</h4>
                  <p>{achievement.description}</p>
                  <span className="AutoboyExpress-premium-earned-badge">
                    Earned {achievement.earnedAt ? new Date(achievement.earnedAt).toLocaleDateString() : ''}
                  </span>
                </div>
              ))
            ) : (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', opacity: 0.6 }}>
                <FontAwesomeIcon icon={faTrophy} size="3x" style={{ marginBottom: '1rem' }} />
                <p>No achievements yet. Keep shopping to earn badges!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Wallet Content
  const renderWalletContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      {/* Wallet Balance Cards */}
      <div className="AutoboyExpress-dash-stats">
        <div className="AutoboyExpress-dash-stat-card" style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: 'white' }}>
          <div className="AutoboyExpress-dash-stat-header">
            <div className="AutoboyExpress-dash-stat-icon" style={{ background: 'rgba(255, 255, 255, 0.2)', color: 'white' }}>
              <FontAwesomeIcon icon={faWallet} />
            </div>
          </div>
          <div className="AutoboyExpress-dash-stat-value" style={{ color: 'white' }}>{formatCurrency(walletBalance.balance)}</div>
          <div className="AutoboyExpress-dash-stat-label" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Available Balance</div>
          <p style={{ fontSize: '0.75rem', marginTop: '0.5rem', opacity: 0.9 }}>
            Your wallet balance from refunds and rewards
          </p>
          <button
            className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
            onClick={() => setShowTopupModal(true)}
            style={{
              marginTop: '1rem',
              width: '100%',
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Top Up Wallet
          </button>
          <button
            className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
            onClick={() => setShowWithdrawModal(true)}
            disabled={!walletBalance.balance}
            style={{
              marginTop: '0.5rem',
              width: '100%',
              background: walletBalance.balance ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.08)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(10px)',
              cursor: walletBalance.balance ? 'pointer' : 'not-allowed',
              opacity: walletBalance.balance ? 1 : 0.6
            }}
          >
            <FontAwesomeIcon icon={faMoneyBillWave} /> Withdraw
          </button>
          <button
            className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
            onClick={() => setShowBankAccounts(true)}
            style={{
              marginTop: '0.5rem',
              width: '100%',
              background: 'rgba(59, 130, 246, 0.3)',
              color: 'white',
              border: '1px solid rgba(59, 130, 246, 0.5)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <FontAwesomeIcon icon={faUniversity} /> Bank Accounts
          </button>
        </div>

        <div className="AutoboyExpress-dash-stat-card" style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', color: 'white' }}>
          <div className="AutoboyExpress-dash-stat-header">
            <div className="AutoboyExpress-dash-stat-icon" style={{ background: 'rgba(255, 255, 255, 0.2)', color: 'white' }}>
              <FontAwesomeIcon icon={faGift} />
            </div>
          </div>
          <div className="AutoboyExpress-dash-stat-value" style={{ color: 'white' }}>{stats.rewardPoints}</div>
          <div className="AutoboyExpress-dash-stat-label" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Reward Points</div>
          <p style={{ fontSize: '0.75rem', marginTop: '0.5rem', opacity: 0.9 }}>
            Earn more points with every purchase
          </p>
        </div>
      </div>

      {/* Transaction History */}
      <div className="AutoboyExpress-dash-content">
        <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Transaction History</h2>
            <div className="AutoboyExpress-dash-filters">
              <button
                className="AutoboyExpress-dash-filter-btn"
                onClick={() => {
                  fetchWalletBalance(true);
                  fetchWalletTransactions();
                }}
                style={{ marginRight: '0.5rem' }}
              >
                <FontAwesomeIcon icon={faHistory} /> Refresh
              </button>
              <button className="AutoboyExpress-dash-filter-btn">
                <FontAwesomeIcon icon={faFilter} /> Filter
              </button>
            </div>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            {isLoadingTransactions ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div className="AutoboyExpress-dash-loading-spinner"></div>
                <p style={{ marginTop: '1rem', color: '#22C55E' }}>Loading transactions...</p>
              </div>
            ) : (
              <TableCard>
                <DataTable
                  columns={[{ width: '14%' }, { width: '15%' }, { width: '31%' }, { width: '15%' }, { width: '12%' }, { width: '13%' }]}
                  minWidth="840px"
                >
                  <THead>
                    <Th>Date</Th>
                    <Th>Type</Th>
                    <Th>Description</Th>
                    <Th align="right">Amount</Th>
                    <Th>Status</Th>
                    <Th>Reference</Th>
                  </THead>
                  <tbody>
                    {Array.isArray(walletTransactions) && walletTransactions.length > 0 ? (
                      walletTransactions.map((transaction, index) => {
                        // Determine if transaction is a credit or debit
                        const isCredit = transaction.type === 'credit' ||
                                       transaction.type === 'topup' ||
                                       transaction.type === 'refund' ||
                                       transaction.type === 'reward' ||
                                       transaction.type === 'escrow_release' ||
                                       (transaction.description && transaction.description.toLowerCase().includes('escrow released')) ||
                                       transaction.amount > 0;

                        const isDebit = transaction.type === 'debit' ||
                                      transaction.type === 'payment' ||
                                      transaction.type === 'purchase' ||
                                      transaction.type === 'withdrawal' ||
                                      transaction.type === 'escrow_hold' ||
                                      (transaction.description && (
                                        transaction.description.toLowerCase().includes('payment') ||
                                        transaction.description.toLowerCase().includes('purchase') ||
                                        transaction.description.toLowerCase().includes('order')
                                      )) ||
                                      (!isCredit && transaction.amount < 0);

                        // Get transaction icon based on type
                        const getTransactionIcon = () => {
                          if (transaction.type === 'topup') return '💰';
                          if (transaction.type === 'refund') return '↩️';
                          if (transaction.type === 'purchase' || transaction.type === 'payment') return '🛍️';
                          if (transaction.type === 'reward') return '🎁';
                          if (transaction.type === 'withdrawal') return '💳';
                          if (transaction.type === 'escrow_release') return '🔓';
                          if (transaction.type === 'escrow_hold') return '🔒';
                          if (isCredit) return '➕';
                          if (isDebit) return '➖';
                          return '💼';
                        };

                        // Format description to be more informative
                        const getDescription = () => {
                          const desc = transaction.description || transaction.note || '';
                          const productName = transaction.product_name || transaction.productName;
                          const orderNumber = transaction.order_number || transaction.orderNumber || transaction.order_id;

                          if (transaction.type === 'escrow_release' || desc.toLowerCase().includes('escrow released')) {
                            if (orderNumber) {
                              return `Escrow Released: ${orderNumber}`;
                            }
                            return desc || 'Escrow Payment Released';
                          }

                          if (transaction.type === 'escrow_hold' || desc.toLowerCase().includes('escrow held')) {
                            if (orderNumber) {
                              return `Escrow Held: ${orderNumber}`;
                            }
                            return desc || 'Payment Held in Escrow';
                          }

                          if (transaction.type === 'purchase' || transaction.type === 'payment') {
                            if (productName && orderNumber) {
                              return `Purchase: ${productName} (Order #${orderNumber.slice(-6)})`;
                            } else if (productName) {
                              return `Purchase: ${productName}`;
                            } else if (orderNumber) {
                              return `Purchase - Order #${orderNumber.slice(-6)}`;
                            } else if (desc) {
                              return desc;
                            }
                            return 'Product Purchase';
                          }

                          if (desc) return desc;
                          if (transaction.type === 'topup') return 'Wallet Top-up';
                          if (transaction.type === 'refund') return 'Order Refund';
                          if (transaction.type === 'reward') return 'Reward Points Conversion';
                          return 'Transaction';
                        };

                        return (
                          <Tr key={transaction.id || transaction._id || index}>
                            <Td className="tw-text-zinc-500 dark:tw-text-zinc-400">{formatDate(transaction.created_at || transaction.date)}</Td>
                            <Td>
                              <Badge tone={isCredit ? 'green' : 'red'}>
                                <span aria-hidden="true">{getTransactionIcon()}</span>
                                {transaction.type || 'Transaction'}
                              </Badge>
                            </Td>
                            <Td>
                              <div className="tw-flex tw-flex-col tw-gap-0.5">
                                <span className="tw-font-medium tw-truncate" title={getDescription()}>{getDescription()}</span>
                                {transaction.metadata?.seller_name && (
                                  <span className="tw-text-xs tw-text-zinc-500 dark:tw-text-zinc-400">
                                    Seller: {transaction.metadata.seller_name}
                                  </span>
                                )}
                              </div>
                            </Td>
                            <Td
                              align="right"
                              className={`tw-font-bold tw-tabular-nums ${isCredit ? 'tw-text-green-600 dark:tw-text-green-400' : 'tw-text-red-500'}`}
                            >
                              {isCredit ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount))}
                            </Td>
                            <Td>
                              <Badge tone={statusTone(transaction.status || 'completed')}>
                                {transaction.status || 'completed'}
                              </Badge>
                            </Td>
                            <Td className="tw-font-mono tw-text-xs tw-text-zinc-500 dark:tw-text-zinc-400">
                              {transaction.reference || transaction.transaction_id ||
                               (transaction.id && `#${transaction.id.slice(-8)}`) || '-'}
                            </Td>
                          </Tr>
                        );
                      })
                    ) : (
                      <TableEmpty colSpan={6} message="No transactions yet — your wallet activity will appear here" />
                    )}
                  </tbody>
                </DataTable>
              </TableCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Settings handlers
  // Handle avatar file selection
  const handleAvatarFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate using the imageUploadService
      const validation = validateImageFile(file, {
        maxSize: 5 * 1024 * 1024, // 5MB
        allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      });

      if (!validation.valid) {
        setToast({ message: validation.error, type: 'error' });
        return;
      }

      setSelectedAvatarFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle avatar upload
  const handleAvatarUpload = async () => {
    try {
      setUploadingAvatar(true);

      let avatarUrl = '';

      if (avatarUploadMethod === 'url') {
        // Use URL directly
        avatarUrl = settingsFormData.avatarUrl;
        if (!avatarUrl || !avatarUrl.startsWith('http')) {
          setToast({ message: 'Please enter a valid image URL', type: 'warning' });
          setUploadingAvatar(false);
          return;
        }
      } else {
        // Upload file using imageUploadService
        if (!selectedAvatarFile) {
          setToast({ message: 'Please select an image file', type: 'error' });
          setUploadingAvatar(false);
          return;
        }

        // Upload to ImgBB/Imgur using the imageUploadService
        const uploadResult = await uploadImage(selectedAvatarFile, (progress) => {
        });

        if (uploadResult && uploadResult.url) {
          avatarUrl = uploadResult.url;
        } else {
          throw new Error('Failed to upload image');
        }
      }

      // Update profile with new avatar - INCLUDE ALL EXISTING DATA to prevent data loss
      const response = await userAPI.updateProfile({
        avatar: avatarUrl,
        // Include existing name data to prevent it from being reset
        first_name: userProfile.firstName || settingsFormData.firstName,
        last_name: userProfile.lastName || settingsFormData.lastName,
        name: userProfile.name,
        username: userProfile.username || userProfile.name,
        phone: userProfile.phone || settingsFormData.phone,
        // Also send as nested profile object
        profile: {
          first_name: userProfile.firstName || settingsFormData.firstName,
          last_name: userProfile.lastName || settingsFormData.lastName,
          avatar: avatarUrl
        }
      });

      // CRITICAL: Update localStorage FIRST before updating state
      // This ensures refresh will load complete data
      const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const completeProfileData = {
        ...cachedUser,
        avatar: avatarUrl,
        // PRESERVE all name fields
        name: userProfile.name || cachedUser.name,
        username: userProfile.username || cachedUser.username,
        first_name: userProfile.firstName || cachedUser.first_name,
        firstName: userProfile.firstName || cachedUser.firstName,
        last_name: userProfile.lastName || cachedUser.last_name,
        lastName: userProfile.lastName || cachedUser.lastName,
        phone: userProfile.phone || cachedUser.phone,
        email: userProfile.email || cachedUser.email,
        profile: {
          ...(cachedUser.profile || {}),
          avatar: avatarUrl,
          first_name: userProfile.firstName || cachedUser.profile?.first_name,
          last_name: userProfile.lastName || cachedUser.profile?.last_name,
          firstName: userProfile.firstName || cachedUser.profile?.firstName,
          lastName: userProfile.lastName || cachedUser.profile?.lastName
        }
      };
      localStorage.setItem('user', JSON.stringify(completeProfileData));

      // Update local state with complete data
      setUserProfile({
        ...userProfile,
        avatar: avatarUrl
      });

      setAvatarPreview(avatarUrl);
      setSettingsFormData({ ...settingsFormData, avatar: avatarUrl, avatarUrl: avatarUrl });
      setEditMode({ ...editMode, avatar: false });
      setSelectedAvatarFile(null);
      // Notify Navbar/AuthContext so avatar updates live
      window.dispatchEvent(new Event('userUpdated'));
      setToast({ message: '✓ Profile picture updated successfully!', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to update profile picture. Please try again.', type: 'error' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    try {
      // Combine first and last name for username/name field
      const fullName = `${settingsFormData.firstName} ${settingsFormData.lastName}`.trim();

      // Update profile via API - send all possible field formats to ensure backend compatibility
      const response = await userAPI.updateProfile({
        // Top-level fields (for compatibility with backend User model)
        name: fullName,
        username: fullName, // Update username to match the full name
        phone: settingsFormData.phone,
        avatar: userProfile.avatar || settingsFormData.avatar, // INCLUDE AVATAR to prevent it from being reset

        // Profile nested fields (snake_case for Go backend)
        first_name: settingsFormData.firstName,
        last_name: settingsFormData.lastName,

        // Also send as nested profile object for extra compatibility
        profile: {
          first_name: settingsFormData.firstName,
          last_name: settingsFormData.lastName,
          avatar: userProfile.avatar || settingsFormData.avatar // INCLUDE AVATAR in nested profile too
        }
      });


      // CRITICAL: Update localStorage FIRST with complete data
      // This ensures refresh will load all fields correctly
      const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const completeProfileData = {
        ...cachedUser,
        // Top-level fields
        name: fullName,
        username: fullName,
        phone: settingsFormData.phone,
        email: userProfile.email || cachedUser.email,
        // CRITICAL: PRESERVE AVATAR
        avatar: userProfile.avatar || settingsFormData.avatar || cachedUser.avatar,
        // Both naming conventions for compatibility
        first_name: settingsFormData.firstName,
        firstName: settingsFormData.firstName,
        last_name: settingsFormData.lastName,
        lastName: settingsFormData.lastName,
        // Nested profile object
        profile: {
          ...(cachedUser.profile || {}),
          first_name: settingsFormData.firstName,
          last_name: settingsFormData.lastName,
          firstName: settingsFormData.firstName,
          lastName: settingsFormData.lastName,
          // CRITICAL: PRESERVE AVATAR in nested profile
          avatar: userProfile.avatar || settingsFormData.avatar || cachedUser.avatar || cachedUser.profile?.avatar
        }
      };
      localStorage.setItem('user', JSON.stringify(completeProfileData));

      // Update local state with complete data
      setUserProfile({
        ...userProfile,
        firstName: settingsFormData.firstName,
        lastName: settingsFormData.lastName,
        name: fullName,
        username: fullName,
        phone: settingsFormData.phone
      });

      setEditMode({ ...editMode, profile: false });
      setToast({ message: '✓ Profile updated successfully!', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to update profile. Please try again.', type: 'error' });
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (settingsFormData.newPassword !== settingsFormData.confirmPassword) {
      setToast({ message: 'New passwords do not match!', type: 'warning' });
      return;
    }
    if (settingsFormData.newPassword.length < 8) {
      setToast({ message: 'Password must be at least 8 characters long!', type: 'warning' });
      return;
    }
    try {
      await userAPI.changePassword({
        currentPassword: settingsFormData.currentPassword,
        newPassword: settingsFormData.newPassword
      });
      setSettingsFormData({ ...settingsFormData, currentPassword: '', newPassword: '', confirmPassword: '' });
      setEditMode({ ...editMode, password: false });
      setToast({ message: 'Password changed successfully!', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to change password. Please check your current password.', type: 'error' });
    }
  };

  // Settings Content
  const renderSettingsContent = () => {
    const tabs = [
      { id: 'profile', label: 'Profile', icon: 'fa-user' },
      { id: 'security', label: 'Security', icon: 'fa-lock' },
      { id: 'notifications', label: 'Notifications', icon: 'fa-bell' },
      { id: 'sessions', label: 'Sessions', icon: 'fa-shield-alt' },
      { id: 'account', label: 'Account', icon: 'fa-cog' },
    ];

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        <div className="bd-settings-layout">
          {/* Sidebar nav */}
          <nav className="bd-settings-nav">
            {tabs.map(t => (
              <button
                key={t.id}
                className={`bd-settings-nav-item${settingsTab === t.id ? ' active' : ''}`}
                onClick={() => setSettingsTab(t.id)}
              >
                <i className={`fas ${t.icon}`} style={{ width: 16 }} />
                {t.label}
              </button>
            ))}
          </nav>

          {/* Panel */}
          <div>
            {/* ── PROFILE ── */}
            {settingsTab === 'profile' && (
              <div className="bd-settings-card">
                <div className="bd-settings-card-title">Profile Information</div>
                <div className="bd-settings-card-sub">Update your personal details</div>

                {/* Avatar row */}
                <div className="bd-settings-avatar-row">
                  <div className="bd-settings-avatar-wrap">
                    <img
                      className="bd-settings-avatar-img"
                      src={avatarPreview || userProfile?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.firstName || 'U')}+${encodeURIComponent(userProfile?.lastName || 'ser')}&background=22C55E&color=fff&size=150`}
                      alt="Avatar"
                      onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.firstName || 'U')}+${encodeURIComponent(userProfile?.lastName || 'ser')}&background=22C55E&color=fff&size=150`; }}
                    />
                  </div>
                  <div className="bd-settings-avatar-controls">
                    <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>{userProfile.firstName || 'User'} {userProfile.lastName || ''}</p>
                    <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: '#22C55E' }}>Premium Member</p>
                    <div className="bd-settings-avatar-toggle">
                      <button type="button" className={`bd-settings-avatar-toggle-btn${avatarUploadMethod === 'url' ? ' active' : ''}`} onClick={() => setAvatarUploadMethod('url')}>URL</button>
                      <button type="button" className={`bd-settings-avatar-toggle-btn${avatarUploadMethod === 'file' ? ' active' : ''}`} onClick={() => setAvatarUploadMethod('file')}>Upload</button>
                    </div>
                    {avatarUploadMethod === 'url' && (
                      <input className="bd-settings-input" style={{ marginTop: '0.5rem' }}
                        type="url" placeholder="https://..." value={settingsFormData.avatarUrl}
                        onChange={e => {
                          setSettingsFormData({ ...settingsFormData, avatarUrl: e.target.value });
                          if (e.target.value.startsWith('http')) setAvatarPreview(e.target.value);
                        }} />
                    )}
                    {avatarUploadMethod === 'file' && (
                      <>
                        <input type="file" ref={avatarFileInputRef} accept="image/*" onChange={handleAvatarFileSelect} style={{ display: 'none' }} />
                        <button className="bd-settings-btn-outline" style={{ marginTop: '0.5rem' }} onClick={() => avatarFileInputRef.current?.click()}>
                          Choose file
                        </button>
                        {selectedAvatarFile && <span style={{ fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{selectedAvatarFile.name}</span>}
                      </>
                    )}
                    <button className="bd-settings-btn-primary" style={{ marginTop: '0.75rem' }}
                      onClick={handleAvatarUpload} disabled={uploadingAvatar}>
                      {uploadingAvatar ? 'Saving...' : 'Save Picture'}
                    </button>
                  </div>
                </div>

                <form className="bd-settings-form" onSubmit={handleProfileSave}>
                  <div className="bd-settings-grid-2">
                    <div className="bd-settings-field">
                      <label>First Name</label>
                      <input className="bd-settings-input" value={settingsFormData.firstName}
                        onChange={e => setSettingsFormData({ ...settingsFormData, firstName: e.target.value })} />
                    </div>
                    <div className="bd-settings-field">
                      <label>Last Name</label>
                      <input className="bd-settings-input" value={settingsFormData.lastName}
                        onChange={e => setSettingsFormData({ ...settingsFormData, lastName: e.target.value })} />
                    </div>
                    <div className="bd-settings-field">
                      <label>Email</label>
                      <input className="bd-settings-input bd-readonly" value={settingsFormData.email} readOnly />
                    </div>
                    <div className="bd-settings-field">
                      <label>Phone</label>
                      <input className="bd-settings-input" value={settingsFormData.phone}
                        onChange={e => setSettingsFormData({ ...settingsFormData, phone: e.target.value })} />
                    </div>
                    <div className="bd-settings-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Bio</label>
                      <textarea className="bd-settings-input" rows={3} value={settingsFormData.bio}
                        onChange={e => setSettingsFormData({ ...settingsFormData, bio: e.target.value })}
                        placeholder="Tell us a bit about yourself..." />
                    </div>
                    <div className="bd-settings-field">
                      <label>Location</label>
                      <input className="bd-settings-input" value={settingsFormData.location} placeholder="City, Country"
                        onChange={e => setSettingsFormData({ ...settingsFormData, location: e.target.value })} />
                    </div>
                    <div className="bd-settings-field">
                      <label>Gender</label>
                      <select className="bd-settings-input" value={settingsFormData.gender}
                        onChange={e => setSettingsFormData({ ...settingsFormData, gender: e.target.value })}>
                        <option value="">Prefer not to say</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="bd-settings-field">
                      <label>Date of Birth</label>
                      <input className="bd-settings-input" type="date" value={settingsFormData.date_of_birth}
                        onChange={e => setSettingsFormData({ ...settingsFormData, date_of_birth: e.target.value })} />
                    </div>
                  </div>
                  <button type="submit" className="bd-settings-btn-primary" style={{ marginTop: '1rem' }}>
                    Save Profile
                  </button>
                </form>

                {/* Default Delivery Address Card */}
                <div style={{ marginTop: '1.5rem', padding: '1rem', borderRadius: '10px', border: '1px solid var(--bd-border)', background: 'var(--bd-bg)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <p style={{ fontWeight: 600, fontSize: '0.9rem', margin: 0 }}>Default Delivery Address</p>
                    <button
                      type="button"
                      className="bd-settings-btn-outline"
                      style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}
                      onClick={() => setActiveSection('addresses')}
                    >
                      Manage Addresses
                    </button>
                  </div>
                  {primaryAddress ? (
                    <p style={{ fontSize: '0.875rem', color: 'var(--bd-muted)', margin: 0, lineHeight: 1.6 }}>
                      {[primaryAddress.street, primaryAddress.city, primaryAddress.state, primaryAddress.country, primaryAddress.postal_code].filter(Boolean).join(', ')}
                      {primaryAddress.landmark && <><br /><span style={{ fontSize: '0.8rem' }}>Landmark: {primaryAddress.landmark}</span></>}
                    </p>
                  ) : (
                    <p style={{ fontSize: '0.875rem', color: '#9ca3af', margin: 0 }}>
                      No default address set. <button type="button" style={{ background: 'none', border: 'none', color: '#22C55E', cursor: 'pointer', padding: 0, fontSize: '0.875rem' }} onClick={() => setActiveSection('addresses')}>Add one →</button>
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── SECURITY ── */}
            {settingsTab === 'security' && (
              <div className="bd-settings-card">
                <div className="bd-settings-card-title">Change Password</div>
                <div className="bd-settings-card-sub">Keep your account secure</div>
                <form className="bd-settings-form" onSubmit={handlePasswordChange}>
                  <div className="bd-settings-field">
                    <label>Current Password</label>
                    <input className="bd-settings-input" type="password" placeholder="Current password"
                      value={settingsFormData.currentPassword}
                      onChange={e => setSettingsFormData({ ...settingsFormData, currentPassword: e.target.value })} />
                  </div>
                  <div className="bd-settings-grid-2">
                    <div className="bd-settings-field">
                      <label>New Password</label>
                      <input className="bd-settings-input" type="password" placeholder="New password (min 8 chars)"
                        value={settingsFormData.newPassword}
                        onChange={e => setSettingsFormData({ ...settingsFormData, newPassword: e.target.value })} />
                    </div>
                    <div className="bd-settings-field">
                      <label>Confirm New Password</label>
                      <input className="bd-settings-input" type="password" placeholder="Repeat new password"
                        value={settingsFormData.confirmPassword}
                        onChange={e => setSettingsFormData({ ...settingsFormData, confirmPassword: e.target.value })} />
                    </div>
                  </div>
                  <button type="submit" className="bd-settings-btn-primary" style={{ marginTop: '0.5rem' }}>
                    Change Password
                  </button>
                </form>
              </div>
            )}

            {/* ── NOTIFICATIONS ── */}
            {settingsTab === 'notifications' && (
              <div className="bd-settings-card">
                <div className="bd-settings-card-title">Notification Preferences</div>
                <div className="bd-settings-card-sub">Choose what alerts you receive</div>
                <ul className="bd-notif-list">
                  {[
                    { key: 'emailNotifications', label: 'Email Notifications', sub: 'Receive updates via email' },
                    { key: 'pushNotifications', label: 'Push Notifications', sub: 'Browser / app push alerts' },
                    { key: 'smsNotifications', label: 'SMS Notifications', sub: 'Text message alerts' },
                    { key: 'orderUpdates', label: 'Order Updates', sub: 'Shipping and delivery notifications' },
                    { key: 'exclusiveDeals', label: 'Exclusive Deals', sub: 'Premium member-only offers' },
                    { key: 'priceDrops', label: 'Price Drops', sub: 'Get notified when prices fall' },
                    { key: 'earlyAccess', label: 'Early Access', sub: 'First look at new arrivals' },
                    { key: 'vipEvents', label: 'VIP Events', sub: 'Exclusive premium member events' },
                    { key: 'marketingEmails', label: 'Marketing Emails', sub: 'Promotions and newsletters' },
                  ].map(({ key, label, sub }) => (
                    <li key={key} className="bd-notif-item">
                      <div>
                        <div className="bd-notif-item-label">{label}</div>
                        <div className="bd-notif-item-sub">{sub}</div>
                      </div>
                      <label className="bd-toggle">
                        <input type="checkbox" checked={notificationPrefs[key]}
                          onChange={e => setNotificationPrefs({ ...notificationPrefs, [key]: e.target.checked })} />
                        <span className="bd-toggle-track"><span className="bd-toggle-thumb" /></span>
                      </label>
                    </li>
                  ))}
                </ul>
                <button className="bd-settings-btn-primary" style={{ marginTop: '1rem' }}
                  onClick={handleSaveNotifPrefs} disabled={savingNotifPrefs}>
                  {savingNotifPrefs ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
            )}

            {/* ── SESSIONS ── */}
            {settingsTab === 'sessions' && (
              <div className="bd-settings-card">
                <div className="bd-settings-card-title">Active Sessions</div>
                <div className="bd-settings-card-sub">Devices currently logged into your account</div>
                {sessionsLoading ? (
                  <p style={{ color: '#6b7280', padding: '1rem 0' }}>Loading sessions...</p>
                ) : activeSessions.length === 0 ? (
                  <p style={{ color: '#6b7280', padding: '1rem 0' }}>No active sessions found.</p>
                ) : (
                  <ul className="bd-session-list">
                    {activeSessions.map(session => (
                      <li key={session.id || session._id} className="bd-session-item">
                        <div>
                          <div className="bd-session-device">
                            <i className="fas fa-desktop" style={{ marginRight: '0.5rem' }} />
                            {session.device_name || session.device || session.user_agent || 'Unknown Device'}
                            {session.is_current && <span className="bd-session-badge">Current</span>}
                          </div>
                          <div className="bd-session-meta">
                            {session.ip_address || session.ip} &bull; Last active: {session.last_active
                              ? new Date(session.last_active).toLocaleString()
                              : 'Unknown'}
                          </div>
                        </div>
                        {!session.is_current && (
                          <button className="bd-settings-btn-danger-outline"
                            onClick={() => handleTerminateSession(session.id || session._id)}
                            disabled={terminatingSession === (session.id || session._id)}>
                            {terminatingSession === (session.id || session._id) ? 'Ending...' : 'End'}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {activeSessions.length > 1 && (
                  <button className="bd-settings-btn-danger-outline" style={{ marginTop: '0.75rem' }}
                    onClick={handleTerminateAllSessions}>
                    Sign out of all other devices
                  </button>
                )}
              </div>
            )}

            {/* ── ACCOUNT ── */}
            {settingsTab === 'account' && (
              <>
                <div className="bd-settings-card">
                  <div className="bd-settings-card-title">Export My Data</div>
                  <div className="bd-settings-card-sub">Download a copy of your account data (GDPR)</div>
                  <button className="bd-settings-btn-outline" style={{ marginTop: '0.75rem' }}
                    onClick={async () => {
                      try {
                        await dataManagementAPI.exportUserData();
                        setToast({ message: 'Data export requested. You will receive an email shortly.', type: 'success' });
                      } catch {
                        setToast({ message: 'Failed to request data export.', type: 'error' });
                      }
                    }}>
                    <i className="fas fa-download" style={{ marginRight: '0.5rem' }} />
                    Request Data Export
                  </button>
                </div>
                <div className="bd-settings-card bd-settings-card-danger" style={{ marginTop: '1rem' }}>
                  <div className="bd-settings-card-title" style={{ color: '#ef4444' }}>Danger Zone</div>
                  <div className="bd-settings-card-sub">Irreversible account actions</div>
                  <button className="bd-settings-btn-danger" style={{ marginTop: '0.75rem' }}
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete your account? This cannot be undone.')) {
                        userAPI.deleteAccount?.()
                          .then(() => { localStorage.clear(); navigate('/'); })
                          .catch(() => setToast({ message: 'Failed to delete account.', type: 'error' }));
                      }
                    }}>
                    <i className="fas fa-trash" style={{ marginRight: '0.5rem' }} />
                    Delete Account
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Wishlist Content
  const renderWishlistContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">
            <FontAwesomeIcon icon={faHeart} style={{ marginRight: '0.5rem', color: '#ef4444' }} />
            My Wishlist
          </h2>
          <button
            className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
            onClick={() => navigate('/shop')}
          >
            <FontAwesomeIcon icon={faShoppingBag} /> Browse Products
          </button>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          {wishlist.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
              <FontAwesomeIcon icon={faHeart} size="3x" style={{ marginBottom: '1rem', opacity: 0.5, color: '#ef4444' }} />
              <p style={{ fontSize: '1.125rem', fontWeight: '500', marginBottom: '0.5rem' }}>Your wishlist is empty</p>
              <p style={{ fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                Save items you love to buy them later and get notified of price drops
              </p>
              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                onClick={() => navigate('/shop')}
              >
                <FontAwesomeIcon icon={faShoppingBag} /> Start Shopping
              </button>
            </div>
          ) : (
            <div className="AutoboyExpress-buyer-wishlist-grid">
              {wishlist.map((item, index) => {
                // Extract product data from wishlist item
                const product = item.product || item;
                const productId = product._id || product.id || item.product_id;
                const productName = product.title || product.name || 'Product';

                // Handle image paths - check multiple possible formats
                let productImage;
                if (product.images && Array.isArray(product.images) && product.images.length > 0) {
                  // If images is an array of objects with url property
                  productImage = product.images[0].url || product.images[0];
                } else if (product.image) {
                  productImage = product.image;
                } else {
                  // Fallback to null - will use CSS gradient
                  productImage = null;
                }

                const productPrice = product.price || 0;
                const productCondition = product.condition || 'New';
                const sellerName = product.seller_premium
                  ? (product.seller_name || product.seller?.name || 'AutoboyExpress Seller')
                  : `Seller ${String(product.seller_id || '000').slice(-3).padStart(3, '0')}`;
                const isAvailable = product.quantity > 0 || product.stock > 0 || true; // Default to available

                return (
                  <div key={productId || index} className="AutoboyExpress-buyer-wishlist-card">
                    <div
                      className="AutoboyExpress-buyer-wishlist-image"
                      onClick={() => navigate(`/product/${productId}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img
                        src={productImage}
                        alt={productName}
                        onError={(e) => {
                          // Fallback if image fails to load
                          e.target.style.background = 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)';
                          e.target.style.display = 'flex';
                          e.target.style.alignItems = 'center';
                          e.target.style.justifyContent = 'center';
                        }}
                      />
                      {/* Premium badge overlay */}
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                        color: '#000',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(255,215,0,0.3)'
                      }}>
                        <FontAwesomeIcon icon={faCrown} /> PREMIUM
                      </div>
                    </div>
                    <div className="AutoboyExpress-buyer-wishlist-info">
                      <h4
                        onClick={() => navigate(`/product/${productId}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        {productName}
                      </h4>
                      <p className="AutoboyExpress-buyer-wishlist-seller">
                        <FontAwesomeIcon icon={faStore} style={{ marginRight: '0.5rem', fontSize: '0.875rem' }} />
                        {sellerName}
                      </p>
                      <p className="AutoboyExpress-buyer-wishlist-condition">
                        <FontAwesomeIcon icon={faTag} style={{ marginRight: '0.5rem', fontSize: '0.875rem' }} />
                        {productCondition.replace('_', ' ').toUpperCase()}
                      </p>
                      <div className="AutoboyExpress-buyer-wishlist-price">{formatCurrency(productPrice)}</div>
                      <div className="AutoboyExpress-buyer-wishlist-actions">
                        <button
                          className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                          disabled={!isAvailable}
                          onClick={() => navigate(`/product/${productId}`)}
                          style={{ flex: 1 }}
                        >
                          <FontAwesomeIcon icon={faEye} /> {isAvailable ? 'View Product' : 'Out of Stock'}
                        </button>
                        <button
                          className="AutoboyExpress-dash-action-btn AutoboyExpress-dash-action-btn-danger"
                          onClick={async () => {
                            try {
                              await wishlistAPI.removeFromWishlist(productId);
                              // Update local state
                              setWishlist(wishlist.filter((_, i) => i !== index));
                              setToast({ message: 'Removed from wishlist', type: 'success' });
                            } catch (error) {
                              setToast({ message: 'Failed to remove from wishlist', type: 'error' });
                            }
                          }}
                          title="Remove from wishlist"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSwapDealsContent = () => {
    const STATUS_LABEL = {
      pending_seller_review:    { text: 'Awaiting Seller', color: '#f59e0b' },
      declined_by_seller:       { text: 'Declined by Seller', color: '#ef4444' },
      pending_initiator_review: { text: 'Seller Responded', color: '#3b82f6' },
      declined_by_initiator:    { text: 'You Declined', color: '#ef4444' },
      accepted_pending_payment: { text: 'Ready to Pay', color: '#10b981' },
      paid_in_escrow:           { text: 'In Escrow', color: '#8b5cf6' },
      shipped:                  { text: 'Shipped', color: '#0ea5e9' },
      delivered:                { text: 'Delivered', color: '#22c55e' },
      completed:                { text: 'Completed', color: '#22c55e' },
      cancelled:                { text: 'Cancelled', color: '#6b7280' },
    };
    const myOffers = getInitiatedSwaps();

    const handleAcceptPrice = async (swapId) => {
      setSwapActionLoading(swapId + '_accept');
      try { await acceptPrice(swapId); } finally { setSwapActionLoading(null); }
    };
    const handleDeclinePrice = async (swapId) => {
      if (!window.confirm('Decline this price offer?')) return;
      setSwapActionLoading(swapId + '_decline');
      try { await declinePrice(swapId); } finally { setSwapActionLoading(null); }
    };
    const handlePayNow = async (swapId) => {
      if (!window.confirm('Pay via wallet to complete this swap?')) return;
      setSwapActionLoading(swapId + '_pay');
      try { await processSwapPayment(swapId, 'wallet'); } finally { setSwapActionLoading(null); }
    };
    const handleCancel = async (swapId) => {
      if (!window.confirm('Cancel this swap offer?')) return;
      setSwapActionLoading(swapId + '_cancel');
      try { await cancelSwap(swapId); } finally { setSwapActionLoading(null); }
    };

    const handleConfirmShipment = async (swapId) => {
      if (!window.confirm('Confirm that you have shipped your device?')) return;
      setSwapActionLoading(swapId + '_ship');
      try { await confirmShipment(swapId, { tracking_number: 'N/A' }); } finally { setSwapActionLoading(null); }
    };

    const handleConfirmDelivery = async (swapId) => {
      if (!window.confirm('Confirm that you have received the device?')) return;
      setSwapActionLoading(swapId + '_deliver');
      try { await confirmDelivery(swapId, true); } finally { setSwapActionLoading(null); }
    };

    const handleCompleteSwap = async (swapId) => {
      if (!window.confirm('Mark this swap as complete? This cannot be undone.')) return;
      setSwapActionLoading(swapId + '_complete');
      try { await completeSwap(swapId); } finally { setSwapActionLoading(null); }
    };

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {[
            { id: 'p2p',    label: 'P2P Swap Deals' },
            { id: 'offers', label: `My Swap Offers${swapMetadata.totalSwaps ? ` (${swapMetadata.totalSwaps})` : ''}` },
          ].map(tab => (
            <button key={tab.id} onClick={() => setSwapOffersTab(tab.id)} style={{
              padding: '8px 18px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '13px',
              background: swapOffersTab === tab.id ? 'var(--primary-color, #2563eb)' : (isDarkMode ? '#1e293b' : '#f1f5f9'),
              color: swapOffersTab === tab.id ? '#fff' : (isDarkMode ? '#94a3b8' : '#475569'), transition: 'all 0.2s',
            }}>{tab.label}</button>
          ))}
        </div>

        {swapOffersTab === 'p2p' && <SwapDeals embedded isDarkMode={isDarkMode} />}

        {swapOffersTab === 'offers' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>My Swap Offers</h3>
              <button onClick={() => setShowSwapOfferForm(true)} style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px',
                border: 'none', cursor: 'pointer', background: 'var(--primary-color, #2563eb)', color: '#fff', fontWeight: 600, fontSize: '13px',
              }}>
                <FontAwesomeIcon icon={faPlus} /> New Swap Offer
              </button>
            </div>

            {swapLoading && (
              <div style={{ textAlign: 'center', padding: '40px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '24px' }} />
              </div>
            )}

            {!swapLoading && myOffers.length === 0 && (
              <div style={{ textAlign: 'center', padding: '48px 24px', borderRadius: '12px', background: isDarkMode ? '#1e293b' : '#f8fafc', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                <FontAwesomeIcon icon={faExchangeAlt} style={{ fontSize: '36px', marginBottom: '12px', opacity: 0.4 }} />
                <p style={{ margin: '0 0 4px', fontWeight: 600 }}>No swap offers yet</p>
                <p style={{ margin: 0, fontSize: '13px' }}>Browse products and click "Swap" to submit a device offer to a seller.</p>
              </div>
            )}

            {!swapLoading && myOffers.map(swap => {
              const statusInfo = STATUS_LABEL[swap.status] || { text: swap.status, color: '#6b7280' };
              const swapId = swap.id || swap._id;
              return (
                <div key={swapId} style={{
                  borderRadius: '12px', padding: '16px 20px', marginBottom: '12px',
                  background: isDarkMode ? '#1e293b' : '#fff',
                  border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                  boxShadow: isDarkMode ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: isDarkMode ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {swap.swap_number || swapId?.slice(-8)?.toUpperCase()}
                      </span>
                      <h4 style={{ margin: '4px 0 2px', fontSize: '15px', fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>
                        {swap.model} · {swap.storage}
                      </h4>
                      <span style={{ fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                        {swap.device_type} · Condition {swap.condition_rating}/10
                        {swap.battery_health != null ? ` · Battery ${swap.battery_health}%` : ''}
                      </span>
                    </div>
                    <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, background: statusInfo.color + '22', color: statusInfo.color }}>
                      {statusInfo.text}
                    </span>
                  </div>

                  {/* Target listing product (what you want) */}
                  {swap.listing_product && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px', padding: '10px 12px', borderRadius: '9px', background: isDarkMode ? '#0f172a' : '#f8fafc', border: `1px solid ${isDarkMode ? '#1e293b' : '#e2e8f0'}` }}>
                      {(swap.listing_product.images?.[0]?.url || swap.listing_product.images?.[0] || swap.listing_product.image) && (
                        <img
                          src={swap.listing_product.images?.[0]?.url || swap.listing_product.images?.[0] || swap.listing_product.image}
                          alt={swap.listing_product.title || swap.listing_product.name}
                          style={{ width: 44, height: 44, borderRadius: 7, objectFit: 'cover', flexShrink: 0 }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div>
                        <div style={{ fontSize: '11px', color: isDarkMode ? '#64748b' : '#94a3b8', marginBottom: '2px' }}>Swapping for</div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>
                          {swap.listing_product.title || swap.listing_product.name || 'Listed product'}
                        </div>
                        {swap.listing_product.price && (
                          <div style={{ fontSize: '12px', color: '#22c55e', fontWeight: 700 }}>
                            ₦{Number(swap.listing_product.price).toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Your device's photos */}
                  {swap.images?.length > 0 && (
                    <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
                      {swap.images.slice(0, 5).map((img, i) => (
                        <img key={i} src={img} alt={`Your device ${i + 1}`}
                          style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}` }}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      ))}
                      {swap.images.length > 5 && (
                        <div style={{ width: '48px', height: '48px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDarkMode ? '#0f172a' : '#f1f5f9', fontSize: '12px', fontWeight: 700, color: isDarkMode ? '#94a3b8' : '#64748b' }}>+{swap.images.length - 5}</div>
                      )}
                    </div>
                  )}

                  {swap.seller_topup_amount != null && (
                    <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '8px', background: isDarkMode ? '#0f172a' : '#f0f9ff', border: `1px solid ${isDarkMode ? '#1e3a5f' : '#bae6fd'}`, fontSize: '13px', color: isDarkMode ? '#93c5fd' : '#0369a1' }}>
                      <strong>Seller's Price:</strong> Top-up ₦{Number(swap.seller_topup_amount).toLocaleString()}
                      {swap.platform_fee != null && <span> · Fee ₦{Number(swap.platform_fee).toLocaleString()} · VAT ₦{Number(swap.vat_amount || 0).toLocaleString()}</span>}
                      {swap.total_payable != null && <span style={{ fontWeight: 700 }}> → Total ₦{Number(swap.total_payable).toLocaleString()}</span>}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
                    {swap.status === 'pending_initiator_review' && (
                      <>
                        <button disabled={swapActionLoading === swapId + '_accept'} onClick={() => handleAcceptPrice(swapId)} style={{ padding: '7px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#10b981', color: '#fff', fontWeight: 600, fontSize: '13px' }}>
                          {swapActionLoading === swapId + '_accept' ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheckCircle} />} Accept Price
                        </button>
                        <button disabled={swapActionLoading === swapId + '_decline'} onClick={() => handleDeclinePrice(swapId)} style={{ padding: '7px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#ef4444', color: '#fff', fontWeight: 600, fontSize: '13px' }}>
                          {swapActionLoading === swapId + '_decline' ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTimes} />} Decline Price
                        </button>
                      </>
                    )}
                    {swap.status === 'accepted_pending_payment' && (
                      <button disabled={swapActionLoading === swapId + '_pay'} onClick={() => handlePayNow(swapId)} style={{ padding: '7px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#2563eb', color: '#fff', fontWeight: 600, fontSize: '13px' }}>
                        {swapActionLoading === swapId + '_pay' ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faWallet} />} Pay via Wallet
                      </button>
                    )}
                    {swap.status === 'paid_in_escrow' && (
                      <button disabled={swapActionLoading === swapId + '_ship'} onClick={() => handleConfirmShipment(swapId)} style={{ padding: '7px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#0ea5e9', color: '#fff', fontWeight: 600, fontSize: '13px' }}>
                        {swapActionLoading === swapId + '_ship' ? <FontAwesomeIcon icon={faSpinner} spin /> : '📦'} Mark as Shipped
                      </button>
                    )}
                    {swap.status === 'shipped' && (
                      <button disabled={swapActionLoading === swapId + '_deliver'} onClick={() => handleConfirmDelivery(swapId)} style={{ padding: '7px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#22c55e', color: '#fff', fontWeight: 600, fontSize: '13px' }}>
                        {swapActionLoading === swapId + '_deliver' ? <FontAwesomeIcon icon={faSpinner} spin /> : '✅'} Confirm Delivery
                      </button>
                    )}
                    {swap.status === 'delivered' && (
                      <button disabled={swapActionLoading === swapId + '_complete'} onClick={() => handleCompleteSwap(swapId)} style={{ padding: '7px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#16a34a', color: '#fff', fontWeight: 600, fontSize: '13px' }}>
                        {swapActionLoading === swapId + '_complete' ? <FontAwesomeIcon icon={faSpinner} spin /> : '🏁'} Complete Swap
                      </button>
                    )}
                    {['pending_seller_review', 'pending_initiator_review'].includes(swap.status) && (
                      <button disabled={swapActionLoading === swapId + '_cancel'} onClick={() => handleCancel(swapId)} style={{ padding: '7px 16px', borderRadius: '7px', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, cursor: 'pointer', background: 'transparent', color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600, fontSize: '13px' }}>
                        {swapActionLoading === swapId + '_cancel' ? <FontAwesomeIcon icon={faSpinner} spin /> : null} Cancel
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {showSwapOfferForm && (
              <div onClick={() => setShowSwapOfferForm(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto', borderRadius: '16px' }}>
                  {!swapOfferProductId ? (
                    <div style={{ background: isDarkMode ? '#1e293b' : '#fff', padding: '24px', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                      <h3 style={{ margin: '0 0 12px', color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>Enter Product ID</h3>
                      <p style={{ margin: '0 0 16px', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>Paste the product listing ID you want to offer your device for.</p>
                      <input type="text" placeholder="e.g. 507f1f77bcf86cd799439011" value={swapOfferProductId} onChange={e => setSwapOfferProductId(e.target.value)}
                        style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: isDarkMode ? '#0f172a' : '#f8fafc', color: isDarkMode ? '#f1f5f9' : '#1e293b' }}
                        onKeyDown={e => e.key === 'Enter' && e.target.value.trim() && setSwapOfferProductId(e.target.value.trim())}
                      />
                      <div style={{ display: 'flex', gap: '8px', marginTop: '14px', justifyContent: 'flex-end' }}>
                        <button onClick={() => setShowSwapOfferForm(false)} style={{ padding: '8px 18px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: 'transparent', cursor: 'pointer', color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600 }}>Cancel</button>
                        <button disabled={!swapOfferProductId.trim()} onClick={() => setSwapOfferProductId(swapOfferProductId.trim())} style={{ padding: '8px 18px', borderRadius: '8px', border: 'none', background: '#2563eb', color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: swapOfferProductId.trim() ? 1 : 0.5 }}>Continue</button>
                      </div>
                    </div>
                  ) : (
                    <SwapOfferForm
                      productId={swapOfferProductId}
                      onSuccess={() => { setShowSwapOfferForm(false); setSwapOfferProductId(''); }}
                      onCancel={() => { setShowSwapOfferForm(false); setSwapOfferProductId(''); }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };


  const renderEscrowContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">
            <FontAwesomeIcon icon={faLock} /> Escrow Analytics & Settings
          </h2>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          <EscrowAnalytics />
          <EscrowSettingsPanel />
          <div style={{ textAlign: 'center', padding: '3rem 1rem', marginTop: '2rem' }}>
            <FontAwesomeIcon icon={faLock} size="3x" style={{ color: '#22C55E', marginBottom: '1.5rem' }} />
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: isDarkMode ? '#F9FAFB' : '#1F2937' }}>How Escrow Protection Works</h3>
            <p style={{ color: isDarkMode ? '#9CA3AF' : '#6B7280', marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem' }}>
              Your payments are held securely in escrow until you confirm delivery. This protects both buyers and sellers in every transaction.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', maxWidth: '800px', margin: '0 auto', textAlign: 'left' }}>
              <div style={{ padding: '1.5rem', background: isDarkMode ? '#1F2937' : '#F9FAFB', borderRadius: '12px', border: `2px solid ${isDarkMode ? '#374151' : '#E5E7EB'}` }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', color: isDarkMode ? '#F9FAFB' : '#1F2937' }}>
                  <FontAwesomeIcon icon={faShoppingCart} style={{ color: '#22C55E' }} />
                  Place Order
                </h4>
                <p style={{ fontSize: '0.9rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Order your item and payment goes to escrow</p>
              </div>
              <div style={{ padding: '1.5rem', background: isDarkMode ? '#1F2937' : '#F9FAFB', borderRadius: '12px', border: `2px solid ${isDarkMode ? '#374151' : '#E5E7EB'}` }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', color: isDarkMode ? '#F9FAFB' : '#1F2937' }}>
                  <FontAwesomeIcon icon={faTruck} style={{ color: '#22C55E' }} />
                  Receive Item
                </h4>
                <p style={{ fontSize: '0.9rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Seller ships the item to your address</p>
              </div>
              <div style={{ padding: '1.5rem', background: isDarkMode ? '#1F2937' : '#F9FAFB', borderRadius: '12px', border: `2px solid ${isDarkMode ? '#374151' : '#E5E7EB'}` }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', color: isDarkMode ? '#F9FAFB' : '#1F2937' }}>
                  <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#22C55E' }} />
                  Confirm & Release
                </h4>
                <p style={{ fontSize: '0.9rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Confirm delivery to release payment to seller</p>
              </div>
            </div>
            <div style={{ marginTop: '2rem', padding: '1rem', background: isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)', borderRadius: '8px', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
              <p style={{ fontSize: '0.9rem', color: isDarkMode ? '#4ADE80' : '#16A34A', margin: 0 }}>
                <FontAwesomeIcon icon={faShieldAlt} /> All your orders are automatically protected with escrow when enabled at checkout
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDisputesContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem', color: '#f59e0b' }} />
            Disputes
          </h2>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          <Disputes />
        </div>
      </div>
    </div>
  );

  const renderSecurityContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-content">
        {/* Two-Factor Authentication */}
        <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
          <TwoFactorAuth isDarkMode={isDarkMode} />
        </div>

        {/* Active Sessions */}
        <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">
              <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: '0.5rem' }} />
              Active Sessions
            </h2>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-secondary"
                style={{ fontSize: '0.8rem' }}
                onClick={async () => {
                  setSessionsLoading(true);
                  try {
                    const res = await userAPI.getActiveSessions();
                    setActiveSessions(res?.data?.sessions || res?.data || []);
                  } catch { setActiveSessions([]); }
                  finally { setSessionsLoading(false); }
                }}
              >
                Refresh
              </button>
              {activeSessions.length > 1 && (
                <button className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-secondary" style={{ fontSize: '0.8rem' }} onClick={handleTerminateAllSessions}>
                  Sign Out Other Devices
                </button>
              )}
            </div>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            {sessionsLoading ? (
              <div style={{ textAlign: 'center', padding: '1.5rem', color: '#6b7280' }}>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading sessions...
              </div>
            ) : activeSessions.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#9ca3af', padding: '1.5rem' }}>No active sessions found. Click Refresh to load.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {activeSessions.map((session, idx) => {
                  const sessionId = session.id || session._id || idx;
                  const isCurrent = session.is_current || session.isCurrent;
                  return (
                    <div key={sessionId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.9rem 1rem', borderRadius: '10px', border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`, background: isCurrent ? 'rgba(34,197,94,0.05)' : 'transparent' }}>
                      <div>
                        <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.2rem', color: isDarkMode ? '#f3f4f6' : '#1f2937' }}>
                          {session.device_name || session.device || session.browser || 'Unknown Device'}
                          {isCurrent && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#22C55E', fontWeight: 700 }}>● This device</span>}
                        </p>
                        <p style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          {session.ip_address || session.ip || ''}{session.location ? ` · ${session.location}` : ''}
                          {session.last_active ? ` · ${new Date(session.last_active).toLocaleString()}` : ''}
                        </p>
                      </div>
                      {!isCurrent && (
                        <button
                          className="AutoboyExpress-dash-btn"
                          style={{ fontSize: '0.8rem', color: '#ef4444', border: '1px solid #ef4444', background: 'transparent', padding: '0.4rem 0.8rem', borderRadius: '6px' }}
                          onClick={() => handleTerminateSession(sessionId)}
                          disabled={terminatingSession === sessionId}
                        >
                          {terminatingSession === sessionId ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Sign Out'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Data Management */}
        <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Data Management</h2>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
              <div style={{ padding: '1.5rem', background: isDarkMode ? '#1f2937' : '#f9fafb', borderRadius: '12px', border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}` }}>
                <h4 style={{ fontWeight: '600', marginBottom: '0.5rem', color: isDarkMode ? '#f3f4f6' : '#1f2937' }}>Export My Data</h4>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>Download a copy of all your account data, orders, and activity.</p>
                <button
                  className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-secondary"
                  style={{ width: '100%' }}
                  onClick={async () => {
                    try {
                      await dataManagementAPI.exportUserData();
                      setToast({ message: 'Data export started. Check your email.', type: 'success' });
                    } catch {
                      setToast({ message: 'Failed to start data export', type: 'error' });
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faBox} /> Export My Data
                </button>
              </div>
              <div style={{ padding: '1.5rem', background: isDarkMode ? '#1f2937' : '#f9fafb', borderRadius: '12px', border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}` }}>
                <h4 style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#ef4444' }}>Danger Zone</h4>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>These actions are permanent and cannot be undone.</p>
                <button
                  className="AutoboyExpress-dash-btn"
                  style={{ width: '100%', background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca' }}
                  onClick={() => {
                    if (window.confirm('Are you sure you want to deactivate your account?')) {
                      setToast({ message: 'Account deactivation request submitted. You can reactivate within 30 days by logging in.', type: 'info' });
                    }
                  }}
                >
                  Deactivate Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const getContentForSection = () => {
    switch (activeSection) {
      case 'dashboard': return renderDashboardContent();
      case 'kyc':
        return (
          <div className="AutoboyExpress-dash-content-wrapper">
            <KYCVerification
              userType="buyer"
              email={userProfile?.email || ''}
              onComplete={() => {
                setToast({
                  message: 'KYC verification submitted successfully! We will review your documents.',
                  type: 'success'
                });
                setTimeout(() => setActiveSection('dashboard'), 2000);
              }}
            />
          </div>
        );
      case 'analytics': return renderAnalyticsContent();
      case 'priority-listings': return renderPriorityListingsContent();
      case 'exclusive-deals': return renderExclusiveDealsContent();
      case 'wishlist': return renderWishlistContent();
      case 'addresses':
        return (
          <AddressManagement
            onToast={(t) => setToast({ message: t.message, type: t.type || 'info' })}
            isDarkMode={isDarkMode}
            onAddressChange={handleAddressChange}
          />
        );
      case 'swap-deals': return renderSwapDealsContent();
      case 'escrow': return renderEscrowContent();
      case 'wallet': return renderWalletContent();
      case 'reports': return renderReportsContent();
      case 'badge': return renderBadgeContent();
      case 'disputes': return renderDisputesContent();
      case 'security': return renderSecurityContent();
      case 'settings': return renderSettingsContent();
      default: return renderDashboardContent();
    }
  };

  // Handle notification mark as read
  const handleMarkNotificationRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, unread: false } : n)
      );
    } catch (error) {
    }
  };

  if (loading) {
    return (
      <>
        <Loader />
      </>
    );
  }

  return (
    <>
      {/* Feedback surface for setToast(...) — this dashboard held toast state
          but never rendered anything for it, so every message it produced was
          invisible (the code used window.alert() instead). */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className={`AutoboyExpress-dash-container ${isDarkMode ? 'AutoboyExpress-dash-dark-mode' : ''}`}>
        <div className={`AutoboyExpress-dash-sidebar ${isMobileSidebarOpen ? 'AutoboyExpress-dash-sidebar-open' : ''}`}>
          <div className="AutoboyExpress-dash-logo"><img src={isDarkMode ? darkLogo : lightLogo} alt="AutoboyExpress" /></div>
          <div className="AutoboyExpress-dash-profile">
            <div className="AutoboyExpress-dash-profile-avatar AutoboyExpress-premium-avatar" style={{ overflow: 'hidden', borderRadius: '50%', background: '#1f2937' }}>
              {(avatarPreview || userProfile?.avatar)
                ? <img src={avatarPreview || userProfile.avatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" style="width:60%;height:60%;fill:#f59e0b;margin:20%"><path d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3z"/></svg>'; }} />
                : <FontAwesomeIcon icon={faCrown} />}
            </div>
            <div className="AutoboyExpress-dash-profile-info">
              <h3 className="AutoboyExpress-dash-seller-name">{userProfile.name}</h3>
              <p className="AutoboyExpress-dash-seller-status AutoboyExpress-premium-status">Premium Buyer</p>
            </div>
          </div>

          <ul className="AutoboyExpress-dash-nav">
            <li className="AutoboyExpress-dash-nav-item">
              <button className="AutoboyExpress-dash-nav-link" onClick={() => { navigate('/homepage'); setIsMobileSidebarOpen(false); }}>
                <FontAwesomeIcon icon={faHome} className="AutoboyExpress-dash-nav-icon" />
                Homepage
              </button>
            </li>
            {[
              { id: 'dashboard', icon: faChartBar, label: 'Dashboard' },
              { id: 'kyc', icon: faShieldAlt, label: 'KYC Verification' },
              { id: 'analytics', icon: faChartPie, label: 'Analytics' },
              { id: 'priority-listings', icon: faBolt, label: 'Priority Listings' },
              { id: 'exclusive-deals', icon: faGift, label: 'Exclusive Deals' },
              { id: 'wishlist', icon: faHeart, label: 'Wishlist' },
              { id: 'addresses', icon: faMapMarkerAlt, label: 'My Addresses' },
              { id: 'swap-deals', icon: faExchangeAlt, label: 'Swap Deals' },
              { id: 'escrow', icon: faLock, label: 'Escrow' },
              { id: 'wallet', icon: faWallet, label: 'Wallet' },
              { id: 'reports', icon: faChartLine, label: 'Reports' },
              { id: 'badge', icon: faAward, label: 'Badge & Rewards' },
              { id: 'disputes', icon: faExclamationTriangle, label: 'Disputes' },
              { id: 'security', icon: faShieldAlt, label: 'Security' },
              { id: 'settings', icon: faCog, label: 'Settings' }
            ].map(item => (
              <li key={item.id} className="AutoboyExpress-dash-nav-item">
                <button className={`AutoboyExpress-dash-nav-link ${activeSection === item.id ? 'active' : ''}`} onClick={() => { setActiveSection(item.id); setIsMobileSidebarOpen(false); }}>
                  <FontAwesomeIcon icon={item.icon} className="AutoboyExpress-dash-nav-icon" />
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {isMobileSidebarOpen && <div className="AutoboyExpress-dash-mobile-overlay" onClick={() => setIsMobileSidebarOpen(false)} />}

        <div className="AutoboyExpress-dash-main">
          <div className="AutoboyExpress-dash-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button className="AutoboyExpress-dash-mobile-toggle" onClick={toggleMobileSidebar}><FontAwesomeIcon icon={faBars} /></button>
              <h1 className="AutoboyExpress-dash-title">{activeSection.charAt(0).toUpperCase() + activeSection.slice(1).replace('-', ' ')}</h1>
            </div>
            <div className="AutoboyExpress-dash-header-actions">
            {/* Notifications */}
            <NotificationsDropdown />

            {/* Theme Toggle */}
            <button className="AutoboyExpress-dash-theme-toggle" onClick={toggleTheme}>
              <FontAwesomeIcon icon={isDarkMode ? faSun : faMoon} />
            </button>

            {/* Profile Picture */}
            <div
              className="AutoboyExpress-dash-profile-picture"
              onClick={() => setActiveSection('settings')}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                overflow: 'hidden',
                border: '2px solid #22C55E',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9',
                marginLeft: '15px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 197, 94, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <img
                src={avatarPreview || userProfile.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile.firstName || 'U')}+${encodeURIComponent(userProfile.lastName || 'ser')}&background=22C55E&color=fff&size=128`}
                alt="Profile"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile.firstName || 'U')}+${encodeURIComponent(userProfile.lastName || 'ser')}&background=22C55E&color=fff&size=128`;
                }}
              />
            </div>

            {/* Settings Dropdown */}
            <div className="AutoboyExpress-dash-settings-wrapper">
              <button
                className="AutoboyExpress-dash-settings-btn"
                onClick={() => {
                  setShowSettings(!showSettings);
                  setShowNotifications(false);
                }}
              >
                <FontAwesomeIcon icon={faCog} />
              </button>

              {showSettings && (
                <div className="AutoboyExpress-dash-dropdown AutoboyExpress-dash-settings-dropdown">
                  <div className="AutoboyExpress-dash-dropdown-content">
                    <button className="AutoboyExpress-dash-dropdown-item" onClick={() => setActiveSection('settings')}>
                      <FontAwesomeIcon icon={faUserCircle} />
                      <span>My Profile</span>
                    </button>
                    <button className="AutoboyExpress-dash-dropdown-item" onClick={() => setActiveSection('badge')}>
                      <FontAwesomeIcon icon={faCrown} />
                      <span>Badge & Rewards</span>
                    </button>
                    <button className="AutoboyExpress-dash-dropdown-item" onClick={() => setActiveSection('settings')}>
                      <FontAwesomeIcon icon={faCog} />
                      <span>Settings</span>
                    </button>
                    <button className="AutoboyExpress-dash-dropdown-item">
                      <FontAwesomeIcon icon={faQuestionCircle} />
                      <span>Premium Support</span>
                    </button>
                    <div className="AutoboyExpress-dash-dropdown-divider"></div>
                    <button className="AutoboyExpress-dash-dropdown-item AutoboyExpress-dash-logout-btn" onClick={handleLogout}>
                      <FontAwesomeIcon icon={faSignOutAlt} />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>

          <div className="AutoboyExpress-dash-content-area">{getContentForSection()}</div>
        </div>
      </div>

      {/* Wallet Top-up Modal */}
      {showTopupModal && (
        <div className="AutoboyExpress-premium-overlay">
          <div className="AutoboyExpress-premium-modal" style={{ maxWidth: '400px' }}>
            <div className="AutoboyExpress-premium-header">
              <div className="AutoboyExpress-premium-title">
                <FontAwesomeIcon icon={faWallet} className="AutoboyExpress-premium-icon" />
                <h2>Top Up Wallet</h2>
              </div>
              <button
                className="AutoboyExpress-premium-close"
                onClick={() => setShowTopupModal(false)}
                disabled={isProcessingTopup}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="AutoboyExpress-premium-content">
              <p style={{ marginBottom: '1.5rem', color: '#6B7280' }}>
                Enter the amount you want to add to your wallet. Minimum amount is ₦100.
              </p>
              <div className="AutoboyExpress-dash-form-group">
                <label>Amount (NGN)</label>
                <input
                  type="number"
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="100"
                  step="100"
                  disabled={isProcessingTopup}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div className="AutoboyExpress-premium-actions" style={{ marginTop: '1.5rem' }}>
                <button
                  className="AutoboyExpress-premium-btn AutoboyExpress-premium-btn-secondary"
                  onClick={() => setShowTopupModal(false)}
                  disabled={isProcessingTopup}
                >
                  Cancel
                </button>
                <button
                  className="AutoboyExpress-premium-btn AutoboyExpress-premium-btn-primary"
                  onClick={handleWalletTopup}
                  disabled={isProcessingTopup || !topupAmount}
                >
                  {isProcessingTopup ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faWallet} />
                      <span>Top Up ₦{topupAmount || '0'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bank Accounts Manager */}
      {showBankAccounts && (
        <BankAccountsManager onClose={() => setShowBankAccounts(false)} />
      )}

      {/* Wallet Withdrawal Modal */}
      <WalletWithdrawModal
        isOpen={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        balance={walletBalance.balance}
        isDarkMode={isDarkMode}
        onSuccess={(amount) => {
          setToast({ message: `✓ Withdrawal of ₦${amount.toLocaleString()} submitted`, type: 'success' });
          fetchWalletBalance();
          fetchWalletTransactions();
        }}
      />
    </>
  );
};

export default PremiumBuyerDashboard;

